import { useEffect, useState } from 'react';
import { useScrollDirection } from '../../hooks/useScrollDirection';
import { useLang, SUPPORTED_LANGUAGES } from '../../i18n';

import LandingPageV2 from './LandingPageV2';
import {
  ArrowRight,
  Users,
  BarChart3,
  Calculator,
  CheckCircle2,
  ChevronRight,
  Crosshair,
  Flame,
  Gamepad2,
  MessageSquare,
  PlayCircle,
  Menu,
  X,
  ShieldCheck,
  SlidersHorizontal,
  Target,
  Trophy,
  Video,
  Zap
} from 'lucide-react';
import './PublicMarketingPages.css';

const SITE_URL = 'https://gameprohub.com.br';
const BRAND_IMAGE = `${SITE_URL}/assets/brand/gorila-logo-transparent.png`;
const SIGNUP_HREF = '/login';

const LANDING_EXTRA = {
  'pt-BR': {
    proofStrip: ['FPS Mobile (Alpha)', 'FPS Mobile (Beta)', 'FPS Mobile (Gamma)', 'Calculadora de sensibilidade', 'Sensi-IA', 'Mi Coach'],
    useCases: ['Minha mira sobe', 'Meu spray abre', 'Erro nas primeiras balas', 'Quero ajustar giroscópio', 'Quero melhorar no ranked', 'Quero treinar squad', 'Quero parar de copiar sensi', 'Quero uma rotina clara', 'Perco tracking no close', 'Minha 3x treme', 'Não sei o que treinar', 'Quero consistência'],
    comparison: {
      eyebrow: 'Método acima de cópia', title: 'Pare de tratar sensibilidade como palpite.',
      text: 'O Game Pro Hub transforma ajuste de mira em processo: você calibra uma base, entende o erro e valida o treino com uma rotina curta.',
      leftTitle: 'Cópia aleatória', rightTitle: 'Game Pro Hub',
      left: ['Não explica o erro', 'Mistura mira, recoil e posicionamento', 'Faz você trocar tudo de uma vez', 'Não cria histórico de evolução'],
      right: ['Calcula uma base de partida', 'Separa sintoma, ajuste e treino', 'Mostra o próximo passo', 'Conecta calculadora, Sensi-IA e Coach']
    },
    footer: {
      tagline: 'Sistema de performance com IA para FPS mobile.',
      intro: 'Calcule sensibilidade, diagnostique erros de mira e treine com método no FPS Mobile (Alpha), FPS Mobile (Beta) e FPS Mobile (Gamma).',
      cols: [
        { title: 'Ferramentas', links: [['Calculadora FPS Mobile (Alpha)', '/calculadora-sensibilidade-pubg-mobile'], ['Calculadora FPS Mobile (Beta)', '/calculadora-free-fire'], ['Sensi-IA', '/sensi-ia'], ['Mi Coach', '/coach-ia']] },
        { title: 'Buscas atendidas', links: [['Sensibilidade FPS Mobile (Alpha)', '/calculadora-sensibilidade-pubg-mobile'], ['Sensibilidade FPS Mobile (Beta)', '/calculadora-free-fire'], ['Treinos para FPS mobile', '/coach-ia'], ['Ajuste de mira com IA', '/sensi-ia']] },
        { title: 'Confiança', links: [['Segurança e privacidade', '/seguranca'], ['Planos', '/#planos'], ['FAQ', '/#faq'], ['Entrar', '/login']] }
      ],
      disclaimer: 'Game Pro Hub é uma plataforma independente. Não somos afiliados ou endossados pelas desenvolvedoras dos jogos suportados.'
    }
  },
  'en-US': {
    proofStrip: ['FPS Mobile (Alpha)', 'FPS Mobile (Beta)', 'FPS Mobile (Gamma)', 'Sensitivity Calculator', 'Sensi-IA', 'Mi Coach'],
    useCases: ['My aim rises', 'My spray opens', 'First bullets miss', 'Gyro adjustment', 'Ranked improvement', 'Squad training', 'Stop copying sensitivity', 'Clear routine', 'I lose close-range tracking', 'My 3x shakes', 'I need a training plan', 'I want consistency'],
    comparison: {
      eyebrow: 'Method over copying', title: 'Stop treating sensitivity like a guess.',
      text: 'Game Pro Hub turns aim adjustment into a process: calibrate a baseline, understand the error, and validate the change with focused training.',
      leftTitle: 'Random copy', rightTitle: 'Game Pro Hub',
      left: ['Does not explain the error', 'Mixes aim, recoil and positioning', 'Pushes you to change everything', 'Creates no improvement history'],
      right: ['Calculates a starting baseline', 'Separates symptom, adjustment and training', 'Shows the next step', 'Connects calculator, Sensi-IA and Coach']
    },
    footer: {
      tagline: 'AI performance system for mobile FPS players.',
      intro: 'Calculate sensitivity, diagnose aim errors and train with method in FPS Mobile (Alpha), FPS Mobile (Beta) and FPS Mobile (Gamma).',
      cols: [
        { title: 'Tools', links: [['FPS Mobile (Alpha) Calculator', '/calculadora-sensibilidade-pubg-mobile'], ['FPS Mobile (Beta) Calculator', '/calculadora-free-fire'], ['Sensi-IA', '/sensi-ia'], ['Mi Coach', '/coach-ia']] },
        { title: 'Search intents', links: [['FPS Mobile (Alpha) sensitivity', '/calculadora-sensibilidade-pubg-mobile'], ['FPS Mobile (Beta) sensitivity', '/calculadora-free-fire'], ['Mobile FPS training', '/coach-ia'], ['AI aim adjustment', '/sensi-ia']] },
        { title: 'Trust', links: [['Security and privacy', '/seguranca'], ['Plans', '/#planos'], ['FAQ', '/#faq'], ['Login', '/login']] }
      ],
      disclaimer: 'Game Pro Hub is an independent platform. We are not affiliated or endorsed by the developers of supported games.'
    }
  },
  'es-ES': {
    proofStrip: ['FPS Mobile (Alpha)', 'FPS Mobile (Beta)', 'FPS Mobile (Gamma)', 'Calculadora de sensibilidad', 'Sensi-IA', 'Mi Coach'],
    useCases: ['Mi mira sube', 'Mi spray se abre', 'Fallo las primeras balas', 'Ajustar giroscopio', 'Mejorar en ranked', 'Entrenar squad', 'Dejar de copiar sensi', 'Rutina clara', 'Pierdo tracking en close', 'Mi 3x tiembla', 'Necesito un plan', 'Quiero consistencia'],
    comparison: {
      eyebrow: 'Método por encima de copia', title: 'Deja de tratar la sensibilidad como adivinanza.',
      text: 'Game Pro Hub convierte el ajuste de mira en proceso: calibra una base, entiende el error y valida el cambio con entrenamiento enfocado.',
      leftTitle: 'Copia aleatoria', rightTitle: 'Game Pro Hub',
      left: ['No explica el error', 'Mezcla mira, recoil y posición', 'Te hace cambiar todo de una vez', 'No crea historial de mejora'],
      right: ['Calcula una base inicial', 'Separa síntoma, ajuste y entrenamiento', 'Muestra el próximo paso', 'Conecta calculadora, Sensi-IA y Coach']
    },
    footer: {
      tagline: 'Sistema de rendimiento con IA para FPS mobile.',
      intro: 'Calcula sensibilidad, diagnostica errores de mira y entrena con método en FPS Mobile (Alpha), FPS Mobile (Beta) y FPS Mobile (Gamma).',
      cols: [
        { title: 'Herramientas', links: [['Calculadora FPS Mobile (Alpha)', '/calculadora-sensibilidade-pubg-mobile'], ['Calculadora FPS Mobile (Beta)', '/calculadora-free-fire'], ['Sensi-IA', '/sensi-ia'], ['Mi Coach', '/coach-ia']] },
        { title: 'Búsquedas', links: [['Sensibilidad FPS Mobile (Alpha)', '/calculadora-sensibilidade-pubg-mobile'], ['Sensibilidad FPS Mobile (Beta)', '/calculadora-free-fire'], ['Entrenamiento FPS mobile', '/coach-ia'], ['Ajuste de mira con IA', '/sensi-ia']] },
        { title: 'Confianza', links: [['Seguridad y privacidad', '/seguranca'], ['Planes', '/#planos'], ['FAQ', '/#faq'], ['Entrar', '/login']] }
      ],
      disclaimer: 'Game Pro Hub es una plataforma independiente. No estamos afiliados o respaldados por los desarrolladores de los juegos compatibles.'
    }
  }
};


const canonicalLanguage = (language) => {
  const value = String(language || '').toLowerCase();
  if (value.startsWith('en')) return 'en-US';
  if (value.startsWith('es')) return 'es-ES';
  return 'pt-BR';
};

const PUBLIC_COPY = {
  'pt-BR': {
    primaryCta: 'Entrar',
    secondaryCta: 'Entrar',
    searchPlaceholder: 'Buscar Sensi-IA, Coach, planos...',
    searchAria: 'Buscar no Game Pro Hub',
    searchButton: 'Buscar',
    mainNavAria: 'Navegação principal',
    nav: [
      { label: 'Como funciona', href: '/#como-funciona', keywords: ['como funciona', 'funciona', 'jornada', 'começar', 'comecar'] },
      { label: 'Ferramentas', href: '/#ferramentas', keywords: ['ferramentas', 'calculadora', 'recursos'] },
      { label: 'Sensi-IA', href: '/sensi-ia', keywords: ['sensi', 'sensi ia', 'sensibilidade', 'mira', 'ajuste'] },
      { label: 'Coach IA', href: '/coach-ia', keywords: ['coach', 'coach ia', 'treino', 'treinar', 'evoluir'] },
      { label: 'Planos', href: '/#planos', keywords: ['planos', 'preço', 'preco', 'pro', 'assinatura'] },
      { label: 'FAQ', href: '/#faq', keywords: ['faq', 'dúvidas', 'duvidas', 'perguntas', 'ajuda'] },
      { label: 'Segurança', href: '/seguranca', keywords: ['segurança', 'seguranca', 'privacidade', 'stripe', 'dados'] }
    ],
    landingRoute: {
      kind: 'landing',
      path: '/',
      title: 'Game Pro Hub | Sistema de evolução para FPS mobile',
      ogTitle: 'Game Pro Hub | Sistema de evolução para FPS mobile',
      description: 'Crie sua conta no Game Pro Hub e treine com a Mi, a IA especialista em FPS mobile: diagnóstico de mira, cálculo de sensibilidade e treino com método.',
      schemaName: 'Game Pro Hub'
    },
    loginRoute: {
      kind: 'login',
      path: '/login',
      title: 'Entrar no Game Pro Hub | Login e cadastro',
      ogTitle: 'Entrar no Game Pro Hub',
      description: 'Acesse sua conta ou crie cadastro no Game Pro Hub para usar calculadora de sensibilidade, Sensi-IA e Coach IA.',
      robots: 'noindex, follow',
      schemaName: 'Game Pro Hub Login'
    },
    hero: {
      eyebrow: 'Calculadora, Sensi-IA e Coach IA para FPS mobile',
      title: 'Calcule sua sensibilidade e treine com IA no Game Pro Hub.',
      text: 'Pare de copiar configuração no escuro. O GPH ajuda você a criar uma base de sensibilidade, entender seus erros de mira e transformar treino em evolução mensurável.',
      howItWorks: 'Ver como funciona',
      metricsAria: 'Recursos principais do Game Pro Hub',
      metrics: [
        { strong: 'Câmera', text: 'sensibilidade e tracking' },
        { strong: 'ADS', text: 'mira e estabilidade' },
        { strong: 'IA', text: 'diagnóstico e treino' }
      ]
    },
    community: {
      eyebrow: 'Usuários usando o GPH',
      title: 'Jogadores já usam o Game Pro Hub para treinar com mais método.',
      body: 'O GPH foi criado para quem joga muito, mas quer parar de depender de sensibilidade copiada, achismo e treino sem diagnóstico.',
      aria: 'Sinais de uso do Game Pro Hub',
      stripAria: 'Perfis de jogadores usando o Game Pro Hub',
      strip: ['Jogadores de FPS Mobile', 'Atletas Competitivos', 'Usuários de Sensi-IA', 'Alunos do Coach IA', 'Competidores casuais'],
      signals: [
        { value: 'GPH', label: 'método para parar de copiar sensibilidade no escuro', detail: 'Uma jornada avançada para jogadores de FPS mobile.' },
        { value: '4', label: 'frentes de evolução em uma conta', detail: 'Calculadora de Sensibilidade, Sensi-IA e Coach IA conectadas.' },
        { value: 'Online', label: 'hub disponível no navegador para revisar ajustes', detail: 'Acesse pelo navegador e volte quando mudar DPI, rotina ou estilo de controle.' },
        { value: '0', label: 'senha do jogo solicitada', detail: 'O GPH orienta configurações e treino sem pedir acesso à sua conta do jogo.' }
      ]
    },
    pain: {
      eyebrow: 'Dor do jogador',
      title: 'Você não precisa jogar mais. Precisa corrigir melhor.',
      body: 'A maior perda de evolução vem de treinar sem saber se o problema está na sensibilidade, no recoil, no posicionamento ou na decisão.',
      cards: [
        { icon: Crosshair, title: 'Mira sem padrão', text: 'Um dia a mira encaixa, no outro passa do alvo. Você joga muito, mas não sabe qual ajuste realmente mudou.' },
        { icon: SlidersHorizontal, title: 'Sensibilidade copiada', text: 'Copiar sensi de pro player ignora seu controle, dedo, DPI, pegada e estilo de luta.' },
        { icon: Trophy, title: 'Treino sem direção', text: 'Partidas e x1 acumulam volume, mas falta diagnóstico para saber o que corrigir primeiro.' }
      ]
    },
    solution: {
      eyebrow: 'Solução do GPH',
      title: 'Uma jornada simples: calcular, ajustar, treinar e medir.',
      body: 'O Game Pro Hub conecta ferramentas que normalmente ficam separadas em uma rotina clara para FPS mobile.',
      items: [
        { icon: Target, title: 'Calcule uma base', text: 'Comece por uma sensibilidade coerente para seu controle, mira e rotina.' },
        { icon: SlidersHorizontal, title: 'Refine com Sensi-IA', text: 'Transforme sinais de erro em pequenos ajustes e valide cada mudança com mais controle.' },
        { icon: MessageSquare, title: 'Treine com Coach IA', text: 'Receba próximas ações para mira, recoil, tomada de decisão e rotina de evolução.' }
      ]
    },
    tools: {
      eyebrow: 'Ecossistema de Ferramentas',
      title: 'Precisão matemática para sua mira. Inteligência para o seu treino.',
      body: 'Ferramentas táticas criadas especificamente para resolver as maiores dores do jogador competitivo de FPS Mobile sem depender de sorte ou tentativa e erro.',
      link: 'Acessar ferramenta',
      cards: [
        { id: 'calculadora-pubg', icon: Crosshair, title: 'Calculadora FPS Mobile (Alpha)', text: 'Calibração matemática de câmera, ADS e giroscópio com precisão tática, sem depender de cópia aleatória.', href: '/login' },
        { id: 'calculadora-ff', icon: Flame, title: 'Calculadora FPS Mobile (Beta)', text: 'Ajuste fino de DPI, sensibilidade geral, mira 2x e 4x para maximizar estabilidade e precisão nos disparos.', href: '/login' },
        { id: 'sensi-ia', icon: SlidersHorizontal, title: 'Diagnóstico Sensi-IA', text: 'Inteligência artificial que analisa sintomas de mira pesada, tremida ou passando da cabeça e indica o ajuste incremental exato.', href: '/sensi-ia' },
        { id: 'coach-ia', icon: Gamepad2, title: 'Mi Coach & Treinos', text: 'Trilhas personalizadas de treino diário para FPS Mobile (Alpha), FPS Mobile (Beta) e FPS Mobile (Gamma) direcionadas para evolução contínua.', href: '/coach-ia' }
      ]
    },
    sensi: {
      title: 'Ajuste fino para quem está cansado de trocar tudo de uma vez.',
      text: 'A Sensi-IA ajuda a interpretar sintomas de mira: passar do alvo, faltar velocidade, perder tracking ou abrir recoil.',
      aria: 'Exemplo de sinais analisados pela Sensi-IA',
      signals: ['Mira pesada', 'Puxada acelerada', 'Recoil abrindo', 'Ajuste incremental']
    },
    coach: {
      title: 'Diagnóstico para transformar gameplay em próxima ação.',
      text: 'O Coach IA organiza falhas de mira, posicionamento, leitura de luta e rotina. Em vez de acumular partidas sem direção, você recebe prioridades para treinar com clareza.',
      link: 'Ver página do Coach IA',
      aria: 'Fluxo do Coach IA',
      steps: ['Gameplay observada', 'Erro principal identificado', 'Treino recomendado', 'Evolução acompanhada']
    },
    proof: {
      eyebrow: 'Prova social',
      title: 'Jogadores diferentes, o mesmo objetivo: parar de treinar no escuro.',
      body: 'O GPH busca criar mais clareza, mais controle sobre cada ajuste e menos decisão tomada no calor da partida.',
      aria: 'Depoimentos de usuários do Game Pro Hub',
      testimonials: [
        { quote: 'Parei de trocar sensi toda semana. Agora testo uma mudança, faço o treino e volto com um sinal claro.', name: 'Rafael M.', role: 'FPS Mobile (Alpha)', initials: 'RM', avatarTone: 'lime', result: 'Menos tentativa e erro' },
        { quote: 'A Mi mostrou que meu erro não era só mira. Eu estava abrindo spray por pressa nas primeiras balas.', name: 'Bianca S.', role: 'FPS Mobile (Beta)', initials: 'BS', avatarTone: 'cyan', result: 'Diagnóstico objetivo' },
        { quote: 'A calculadora virou minha base quando mudo HUD ou volto a jogar depois de alguns dias.', name: 'Lucas P.', role: 'FPS Mobile (Gamma)', initials: 'LP', avatarTone: 'violet', result: 'Base reutilizável' },
        { quote: 'O treino curto fez mais diferença do que jogar várias ranked sem saber exatamente o que corrigir.', name: 'Marina K.', role: 'FPS Mobile (Alpha)', initials: 'MK', avatarTone: 'amber', result: 'Rotina clara' }
      ]
    },
    plans: {
      eyebrow: 'Planos',
      title: 'Entre grátis e escolha o nível quando fizer sentido.',
      body: 'A compra e o acesso continuam pelo fluxo seguro existente.',
      create: 'Criar conta',
      items: [
        { badge: 'Comece', name: 'Conta grátis', price: 'R$ 0', text: 'Cadastro para entrar no hub e testar recursos disponíveis.', features: ['Acesso ao hub', 'Base para evolução', 'Ferramentas conectadas'] },
        { badge: 'Mais completo', name: 'Pro', price: 'R$ 29,99/mês', text: 'Libera Coach IA, Sensi-IA e cálculos avançados ilimitados.', features: ['Mi Coach IA completa', 'Sensi-IA liberada', 'Cálculos avançados'] },
        { badge: 'No Pro', name: 'Calculadoras', price: 'Incluso no Pro', text: 'As calculadoras de sensibilidade agora fazem parte do Pro. Quem comprou acesso vitalício antes mantém o direito.', features: ['Camera / ADS / Gyro', 'Calibração avançada', 'Legado preservado'] },
        { badge: 'No app', name: 'Other tools available inside the app', price: 'In-app', text: 'Ferramentas exclusivas de diagnóstico e treino tático no ecossistema interno.', features: ['Acesso à plataforma total', 'Módulos de evolução', 'Acompanhamento contínuo'] }
      ]
    },
    faq: {
      eyebrow: 'FAQ',
      title: 'Tudo que o jogador quer saber antes de criar conta.',
      body: 'Respostas diretas sobre segurança, conta grátis, Pro, sensibilidade, IA, treino e próximo passo.',
      asideAria: 'Resumo para criar conta no Game Pro Hub',
      asideTitle: 'Ainda em dúvida?',
      asideText: 'Comece grátis, calcule sua base e veja se o método do GPH faz sentido para sua mira antes de escolher qualquer plano.',
      asideBullets: ['Sem senha do jogo', 'Conta grátis para começar', 'Ajuste manual e controlado', 'Pensado para FPS mobile'],
      items: [
        { q: 'O que eu ganho ao criar uma conta grátis?', a: 'Você entra no hub, começa pela calculadora e entende qual ferramenta usar primeiro.' },
        { q: 'A calculadora entrega uma sensibilidade perfeita?', a: 'Não existe sensibilidade perfeita universal. O GPH entrega uma base mais inteligente para testar.' },
        { q: 'Por que não copiar sensibilidade de pro player?', a: 'Porque controle, tela, DPI, pegada, Gyro e estilo de luta mudam o resultado.' },
        { q: 'O GPH mexe na minha conta do jogo?', a: 'Não. O Game Pro Hub não pede senha do jogo nem acesso externo.' },
        { q: 'A Sensi-IA muda minha configuração automaticamente?', a: 'Não. Ela orienta ajustes pequenos para você aplicar manualmente.' },
        { q: 'O Coach IA serve para iniciante?', a: 'Sim. Ele ajuda a organizar o que treinar primeiro e como revisar evolução.' }
      ]
    },
    final: {
      title: 'Calcule sua base antes do próximo treino.',
      text: 'Crie sua conta grátis e comece com uma sensibilidade mais consciente.'
    },
    seo: {
      backHome: 'Voltar para o Game Pro Hub',
      panelTitle: 'O que você encontra aqui',
      panelAriaPrefix: 'Resumo de',
      nextEyebrow: 'Próximo passo',
      nextTitle: 'Transforme o cálculo em rotina de evolução.',
      nextBody: 'O cadastro leva você para o hub, onde as ferramentas públicas se conectam com histórico, trilhas e recomendações de treino.',
      haveAccount: 'Já tenho conta'
    }
  },
  'en-US': {
    primaryCta: 'Sign in',
    secondaryCta: 'Sign in',
    searchPlaceholder: 'Search Sensi-IA, Coach, plans...',
    searchAria: 'Search Game Pro Hub',
    searchButton: 'Search',
    mainNavAria: 'Main navigation',
    nav: [
      { label: 'How it works', href: '/#como-funciona', keywords: ['how it works', 'start', 'journey'] },
      { label: 'Tools', href: '/#ferramentas', keywords: ['tools', 'calculator', 'features'] },
      { label: 'Sensi-IA', href: '/sensi-ia', keywords: ['sensi', 'sensitivity', 'aim', 'adjust'] },
      { label: 'Coach IA', href: '/coach-ia', keywords: ['coach', 'training', 'improve'] },
      { label: 'Plans', href: '/#planos', keywords: ['plans', 'price', 'pro', 'subscription'] },
      { label: 'FAQ', href: '/#faq', keywords: ['faq', 'questions', 'help'] },
      { label: 'Security', href: '/security', keywords: ['security', 'privacy', 'stripe', 'data'] }
    ],
    landingRoute: {
      kind: 'landing',
      path: '/',
      title: 'Game Pro Hub | Sensitivity Calculator and AI Training',
      ogTitle: 'Game Pro Hub: sensitivity, aim and AI training',
      description: 'Calculate your sensitivity, adjust your aim with Sensi-IA and improve in competitive mobile FPS with Game Pro Hub.',
      schemaName: 'Game Pro Hub'
    },
    loginRoute: {
      kind: 'login',
      path: '/login',
      title: 'Sign in to Game Pro Hub',
      ogTitle: 'Sign in to Game Pro Hub',
      description: 'Access your account or create a Game Pro Hub account to use sensitivity calculators, Sensi-IA and Coach IA.',
      robots: 'noindex, follow',
      schemaName: 'Game Pro Hub Login'
    },
    hero: {
      eyebrow: 'Calculator, Sensi-IA and Coach IA for mobile FPS',
      title: 'Calculate your sensitivity and train with AI in Game Pro Hub.',
      text: 'Stop copying settings blindly. GPH helps you build a sensitivity baseline, understand aim mistakes and turn practice into measurable progress.',
      howItWorks: 'See how it works',
      metricsAria: 'Main Game Pro Hub features',
      metrics: [
        { strong: 'Camera', text: 'sensitivity and tracking' },
        { strong: 'ADS', text: 'aim stability and precision' },
        { strong: 'AI', text: 'diagnosis and training' }
      ]
    },
    community: {
      eyebrow: 'Players using GPH',
      title: 'Players use Game Pro Hub to train with a clearer method.',
      body: 'GPH is built for players who practice a lot but want to stop relying on copied sensitivity, guesses and training without diagnosis.',
      aria: 'Game Pro Hub usage signals',
      stripAria: 'Player profiles using Game Pro Hub',
      strip: ['Mobile FPS players', 'Tournament Competitors', 'Sensi-IA users', 'Coach IA students', 'Casual competitors'],
      signals: [
        { value: 'GPH', label: 'method to stop copying sensitivity blindly', detail: 'An advanced tactical journey for mobile FPS players.' },
        { value: '4', label: 'progress fronts in one account', detail: 'Sensitivity Calculator, Sensi-IA and Coach IA connected.' },
        { value: '24/7', label: 'online hub to review adjustments', detail: 'Use it in the browser and return when you change DPI, routine or control style.' },
        { value: '0', label: 'game passwords requested', detail: 'GPH guides settings and training without asking for game account access.' }
      ]
    },
    pain: {
      eyebrow: 'Player pain',
      title: 'You do not need to play more. You need to correct better.',
      body: 'Most progress is lost when training without knowing if the issue is sensitivity, recoil, positioning or decision-making.',
      cards: [
        { icon: Crosshair, title: 'Inconsistent aim', text: 'One day aim feels right, the next it overshoots. You play a lot but cannot tell what changed.' },
        { icon: SlidersHorizontal, title: 'Copied sensitivity', text: 'Copying a pro player ignores your control style, finger control, DPI, grip and fight style.' },
        { icon: Trophy, title: 'Training without direction', text: 'Matches and duels add volume, but you still need diagnosis to know what to fix first.' }
      ]
    },
    solution: {
      eyebrow: 'GPH solution',
      title: 'A simple journey: calculate, adjust, train and measure.',
      body: 'Game Pro Hub connects tools that are usually separated into a clear routine for mobile FPS.',
      items: [
        { icon: Target, title: 'Calculate a baseline', text: 'Start with a coherent baseline for your control style, aim and routine.' },
        { icon: SlidersHorizontal, title: 'Refine with Sensi-IA', text: 'Turn error signals into small adjustments and validate each change with more control.' },
        { icon: MessageSquare, title: 'Train with Coach IA', text: 'Get next actions for aim, recoil, decision-making and progress routines.' }
      ]
    },
    tools: {
      eyebrow: 'Tool Ecosystem',
      title: 'Mathematical precision for your aim. Intelligence for your training.',
      body: 'Tactical tools specifically engineered to solve the core pain points of mobile FPS competitive players without guessing.',
      link: 'Access tool',
      cards: [
        { id: 'calculadora-pubg', icon: Crosshair, title: 'FPS Mobile (Alpha) Calculator', text: 'Mathematical calibration for camera, ADS and Gyro with tactical precision, without copying random settings.', href: '/login' },
        { id: 'calculadora-ff', icon: Flame, title: 'FPS Mobile (Beta) Calculator', text: 'Fine-tuning for DPI, general sensitivity, 2x and 4x scopes to maximize stability and headshot accuracy.', href: '/login' },
        { id: 'sensi-ia', icon: SlidersHorizontal, title: 'Sensi-IA Diagnosis', text: 'Artificial intelligence that diagnoses heavy, shaky or overshooting aim symptoms and provides exact incremental fixes.', href: '/sensi-ia' },
        { id: 'coach-ia', icon: Gamepad2, title: 'Mi Coach & Training', text: 'Custom daily training tracks for FPS Mobile (Alpha), FPS Mobile (Beta) and FPS Mobile (Gamma) focused on continuous competitive growth.', href: '/coach-ia' }
      ]
    },
    sensi: {
      title: 'Fine tuning for players tired of changing everything at once.',
      text: 'Sensi-IA helps interpret aim symptoms: overshooting, lacking speed, losing tracking or recoil opening up.',
      aria: 'Example signals analyzed by Sensi-IA',
      signals: ['Heavy aim', 'Fast pull', 'Recoil opening', 'Incremental adjustment']
    },
    coach: {
      title: 'Diagnosis that turns gameplay into a next action.',
      text: 'Coach IA organizes aim, positioning, fight reading and routine issues. Instead of stacking matches without direction, you get clear training priorities.',
      link: 'View Coach IA page',
      aria: 'Coach IA flow',
      steps: ['Gameplay observed', 'Main mistake identified', 'Training recommended', 'Progress tracked']
    },
    proof: {
      eyebrow: 'Social proof',
      title: 'Different players, same goal: stop training blindly.',
      body: 'GPH aims to create more clarity, more control over each adjustment and less heat-of-the-match decision-making.',
      aria: 'Game Pro Hub user testimonials',
      testimonials: [
        { quote: 'I stopped changing sensitivity every week. Now I test one change, run the drill, and come back with a clear signal.', name: 'Rafael M.', role: 'FPS Mobile (Alpha)', initials: 'RM', avatarTone: 'lime', result: 'Less trial and error' },
        { quote: 'Mi showed me the issue was not only aim. My spray opened because I rushed the first bullets.', name: 'Bianca S.', role: 'FPS Mobile (Beta)', initials: 'BS', avatarTone: 'cyan', result: 'Clearer diagnosis' },
        { quote: 'The calculator became my baseline whenever I change HUD or return after a few days away.', name: 'Lucas P.', role: 'FPS Mobile (Gamma)', initials: 'LP', avatarTone: 'violet', result: 'Reusable baseline' },
        { quote: 'A short drill helped more than playing ranked for hours without knowing what to fix.', name: 'Marina K.', role: 'FPS Mobile (Alpha)', initials: 'MK', avatarTone: 'amber', result: 'Clear routine' }
      ]
    },
    plans: {
      eyebrow: 'Plans',
      title: 'Start free and choose the level when it makes sense.',
      body: 'Purchases and access continue through the existing secure checkout flow.',
      create: 'Create account',
      items: [
        { badge: 'Start', name: 'Free account', price: 'Free', text: 'Create an account to enter the hub and test available resources.', features: ['Hub access', 'Progress baseline', 'Connected tools'] },
        { badge: 'Complete', name: 'Pro', price: 'R$ 29,99/mês', text: 'Unlocks Coach IA, Sensi-IA and unlimited advanced tactical calculator usage.', features: ['Full Coach IA', 'Sensi-IA unlocked', 'Advanced tactical calculation'] },
        { badge: 'In Pro', name: 'Calculators', price: 'Included in Pro', text: 'Sensitivity calculators are now part of Pro. Users who bought lifetime access before keep their right.', features: ['Camera / ADS / Gyro', 'Advanced calibration', 'Legacy preserved'] },
        { badge: 'In app', name: 'Other tools available inside the app', price: 'In-app', text: 'Exclusive training and tactical aim tools available inside the platform.', features: ['Full platform access', 'Aim training modules', 'Continuous tracking'] }
      ]
    },
    faq: {
      eyebrow: 'FAQ',
      title: 'What players want to know before creating an account.',
      body: 'Direct answers about security, free account, Pro, sensitivity, AI, training and next steps.',
      asideAria: 'Summary for creating a Game Pro Hub account',
      asideTitle: 'Still unsure?',
      asideText: 'Start free, calculate your baseline and see if the GPH method makes sense for your aim before choosing any plan.',
      asideBullets: ['No game password', 'Free account to start', 'Manual controlled adjustments', 'Built for mobile FPS'],
      items: [
        { q: 'What do I get with a free account?', a: 'You enter the hub, start with the calculator and understand which tool to use first.' },
        { q: 'Does the calculator give perfect sensitivity?', a: 'No universal perfect sensitivity exists. GPH gives you a smarter baseline to test.' },
        { q: 'Why not copy pro player sensitivity?', a: 'Because control style, screen, DPI, grip, Gyro and fight style change the result.' },
        { q: 'Does GPH access my game account?', a: 'No. Game Pro Hub never asks for your game password or external access.' },
        { q: 'Does Sensi-IA change settings automatically?', a: 'No. It guides small adjustments that you apply manually.' },
        { q: 'Is Coach IA useful for beginners?', a: 'Yes. It helps organize what to train first and how to review progress.' }
      ]
    },
    final: {
      title: 'Calculate your baseline before the next training session.',
      text: 'Create a free account and start with more conscious sensitivity.'
    },
    seo: {
      backHome: 'Back to Game Pro Hub',
      panelTitle: 'What you will find here',
      panelAriaPrefix: 'Summary of',
      nextEyebrow: 'Next step',
      nextTitle: 'Turn the calculation into a progress routine.',
      nextBody: 'Your account takes you to the hub, where public tools connect with history, tracks and training recommendations.',
      haveAccount: 'I already have an account'
    }
  },
  'es-ES': {
    primaryCta: 'Entrar',
    secondaryCta: 'Entrar',
    searchPlaceholder: 'Buscar Sensi-IA, Coach, planes...',
    searchAria: 'Buscar en Game Pro Hub',
    searchButton: 'Buscar',
    mainNavAria: 'Navegación principal',
    nav: [
      { label: 'Cómo funciona', href: '/#como-funciona', keywords: ['como funciona', 'cómo funciona', 'empezar', 'jornada'] },
      { label: 'Herramientas', href: '/#ferramentas', keywords: ['herramientas', 'calculadora', 'recursos'] },
      { label: 'Sensi-IA', href: '/sensi-ia', keywords: ['sensi', 'sensibilidad', 'mira', 'ajuste'] },
      { label: 'Coach IA', href: '/coach-ia', keywords: ['coach', 'entrenamiento', 'mejorar'] },
      { label: 'Planes', href: '/#planos', keywords: ['planes', 'precio', 'pro', 'suscripción'] },
      { label: 'FAQ', href: '/#faq', keywords: ['faq', 'preguntas', 'ayuda'] },
      { label: 'Seguridad', href: '/seguridad', keywords: ['seguridad', 'privacidad', 'stripe', 'datos'] }
    ],
    landingRoute: {
      kind: 'landing',
      path: '/',
      title: 'Game Pro Hub | Calculadora de sensibilidad y entrenamiento con IA',
      ogTitle: 'Game Pro Hub: sensibilidad, mira y entrenamiento con IA',
      description: 'Calcula tu sensibilidad, ajusta tu mira con Sensi-IA y mejora en el competitivo de FPS mobile con Game Pro Hub.',
      schemaName: 'Game Pro Hub'
    },
    loginRoute: {
      kind: 'login',
      path: '/login',
      title: 'Entrar en Game Pro Hub',
      ogTitle: 'Entrar en Game Pro Hub',
      description: 'Accede a tu cuenta o crea una cuenta en Game Pro Hub para usar calculadoras de sensibilidad, Sensi-IA y Coach IA.',
      robots: 'noindex, follow',
      schemaName: 'Game Pro Hub Login'
    },
    hero: {
      eyebrow: 'Calculadora, Sensi-IA y Coach IA para FPS mobile',
      title: 'Calcula tu sensibilidad y entrena con IA en Game Pro Hub.',
      text: 'Deja de copiar configuraciones a ciegas. GPH te ayuda a crear una base de sensibilidad, entender tus errores de mira y convertir el entrenamiento en progreso medible.',
      howItWorks: 'Ver cómo funciona',
      metricsAria: 'Recursos principales de Game Pro Hub',
      metrics: [
        { strong: 'Cámara', text: 'sensibilidad y tracking' },
        { strong: 'ADS', text: 'estabilidad de mira' },
        { strong: 'IA', text: 'diagnóstico y entrenamiento' }
      ]
    },
    community: {
      eyebrow: 'Usuarios usando GPH',
      title: 'Jugadores usan Game Pro Hub para entrenar con más método.',
      body: 'GPH fue creado para quienes juegan mucho, pero quieren dejar de depender de sensibilidad copiada, suposiciones y entrenamiento sin diagnóstico.',
      aria: 'Señales de uso de Game Pro Hub',
      stripAria: 'Perfiles de jugadores usando Game Pro Hub',
      strip: ['Jugadores de FPS Mobile', 'Competidores de Torneo', 'Usuarios de Sensi-IA', 'Alumnos de Coach IA', 'Competitivos casuales'],
      signals: [
        { value: 'GPH', label: 'método para dejar de copiar sensibilidad a ciegas', detail: 'Una jornada táctica avanzada para jugadores de FPS mobile.' },
        { value: '4', label: 'frentes de evolución en una cuenta', detail: 'Calculadora de Sensibilidad, Sensi-IA y Coach IA conectadas.' },
        { value: '24/7', label: 'hub online para revisar ajustes', detail: 'Accede desde el navegador y vuelve cuando cambies DPI, rutina o estilo de control.' },
        { value: '0', label: 'contraseñas del juego solicitadas', detail: 'GPH guía configuraciones y entrenamiento sin pedir acceso a tu cuenta del juego.' }
      ]
    },
    pain: {
      eyebrow: 'Dolor del jugador',
      title: 'No necesitas jugar más. Necesitas corregir mejor.',
      body: 'La mayor pérdida de progreso aparece cuando entrenas sin saber si el problema es sensibilidad, recoil, posicionamiento o decisión.',
      cards: [
        { icon: Crosshair, title: 'Mira inconsistente', text: 'Un día la mira encaja, otro día pasa del objetivo. Juegas mucho, pero no sabes qué ajuste cambió.' },
        { icon: SlidersHorizontal, title: 'Sensibilidad copiada', text: 'Copiar la sensi de un pro ignora tu control, dedo, DPI, agarre y estilo de pelea.' },
        { icon: Trophy, title: 'Entreno sin dirección', text: 'Partidas y duelos suman volumen, pero falta diagnóstico para saber qué corregir primero.' }
      ]
    },
    solution: {
      eyebrow: 'Solución GPH',
      title: 'Una jornada simple: calcular, ajustar, entrenar y medir.',
      body: 'Game Pro Hub conecta herramientas que normalmente están separadas en una rutina clara para FPS mobile.',
      items: [
        { icon: Target, title: 'Calcula una base', text: 'Empieza con una sensibilidad coherente para tu control, mira y rutina.' },
        { icon: SlidersHorizontal, title: 'Refina con Sensi-IA', text: 'Convierte señales de error en pequeños ajustes y valida cada cambio con más control.' },
        { icon: MessageSquare, title: 'Entrena con Coach IA', text: 'Recibe próximas acciones para mira, recoil, decisión y rutina de evolución.' }
      ]
    },
    tools: {
      eyebrow: 'Herramientas públicas',
      title: 'Empieza por tu sensibilidad. Evoluciona con IA.',
      body: 'Cada página explica cuándo usar la herramienta y cómo entra en tu entrenamiento.',
      link: 'Conocer herramienta',
      cards: [
        { id: 'calculadora-sensibilidade', icon: Target, title: 'Sensitivity Calculator', text: 'Base matemática para cámara, ADS y Gyro, pensada para reducir pruebas al azar en tu contexto de juego.', href: '/login' },
        { id: 'sensi-ia', icon: SlidersHorizontal, title: 'Sensi-IA Diagnosis', text: 'Capa de ajuste fino para entender si la mira está pesada, acelerada o inestable.', href: '/sensi-ia' },
        { id: 'coach-ia', icon: MessageSquare, title: 'Mi Coach IA', text: 'Diagnóstico y rutas tácticas de entrenamiento para transformar gameplay en progreso competitivo.', href: '/coach-ia' }
      ]
    },
    sensi: {
      title: 'Ajuste fino para quien está cansado de cambiar todo a la vez.',
      text: 'Sensi-IA ayuda a interpretar síntomas de mira: pasar del objetivo, faltar velocidad, perder tracking o abrir recoil.',
      aria: 'Ejemplo de señales analizadas por Sensi-IA',
      signals: ['Mira pesada', 'Pull acelerado', 'Recoil abriendo', 'Ajuste incremental']
    },
    coach: {
      title: 'Diagnóstico para convertir gameplay en próxima acción.',
      text: 'Coach IA organiza errores de mira, posicionamento, lectura de pelea y rutina. En vez de acumular partidas sin dirección, recibes prioridades claras.',
      link: 'Ver página de Coach IA',
      aria: 'Flujo de Coach IA',
      steps: ['Gameplay observado', 'Error principal identificado', 'Entreno recomendado', 'Evolución acompañada']
    },
    proof: {
      eyebrow: 'Prueba social',
      title: 'Jugadores diferentes, el mismo objetivo: dejar de entrenar a ciegas.',
      body: 'GPH busca crear más claridad, más control sobre cada ajuste y menos decisiones tomadas en el calor de la partida.',
      aria: 'Testimonios de usuarios de Game Pro Hub',
      testimonials: [
        { quote: 'Dejé de cambiar sensi cada semana. Ahora pruebo un ajuste, hago el entrenamiento y vuelvo con una señal clara.', name: 'Rafael M.', role: 'FPS Mobile (Alpha)', initials: 'RM', avatarTone: 'lime', result: 'Menos prueba y error' },
        { quote: 'Mi me mostró que el error no era solo mira. El spray se abría porque apuraba las primeras balas.', name: 'Bianca S.', role: 'FPS Mobile (Beta)', initials: 'BS', avatarTone: 'cyan', result: 'Diagnóstico claro' },
        { quote: 'La calculadora se volvió mi base cuando cambio HUD o vuelvo después de algunos días.', name: 'Lucas P.', role: 'FPS Mobile (Gamma)', initials: 'LP', avatarTone: 'violet', result: 'Base reutilizable' },
        { quote: 'Un entrenamiento corto ayudó más que jugar ranked durante horas sin saber qué corregir.', name: 'Marina K.', role: 'FPS Mobile (Alpha)', initials: 'MK', avatarTone: 'amber', result: 'Rutina clara' }
      ]
    },
    plans: {
      eyebrow: 'Planes',
      title: 'Entra gratis y elige el nivel cuando tenga sentido.',
      body: 'La compra y el acceso siguen por el flujo seguro existente.',
      create: 'Crear cuenta',
      items: [
        { badge: 'Empieza', name: 'Cuenta gratis', price: 'Gratis', text: 'Crea cuenta para entrar al hub y probar recursos disponibles.', features: ['Acceso al hub', 'Base para evolução', 'Herramientas conectadas'] },
        { badge: 'Más completo', name: 'Pro', price: 'R$ 29,99/mês', text: 'Libera Coach IA, Sensi-IA y cálculos tácticos avançados ilimitados.', features: ['Coach IA completo', 'Sensi-IA liberada', 'Cálculos tácticos'] },
        { badge: 'En Pro', name: 'Calculadoras', price: 'Incluido en Pro', text: 'Las calculadoras de sensibilidad ahora son parte del Pro. Quien compró acceso vitalicio antes mantiene su derecho.', features: ['Cámara / ADS / Gyro', 'Calibración avanzada', 'Legado preservado'] },
        { badge: 'En app', name: 'Other tools available inside the app', price: 'In-app', text: 'Herramientas exclusivas de diagnóstico y entrenamiento en la plataforma interna.', features: ['Acceso total plataforma', 'Módulos de mira', 'Seguimiento continuo'] }
      ]
    },
    faq: {
      eyebrow: 'FAQ',
      title: 'Lo que el jugador quiere saber antes de crear cuenta.',
      body: 'Respuestas directas sobre seguridad, cuenta gratis, Pro, sensibilidad, IA, entrenamiento y próximo paso.',
      asideAria: 'Resumen para crear cuenta en Game Pro Hub',
      asideTitle: '¿Aún tienes dudas?',
      asideText: 'Empieza gratis, calcula tu base y ve si el método GPH tiene sentido para tu mira antes de elegir un plan.',
      asideBullets: ['Sin contraseña del juego', 'Cuenta gratis para empezar', 'Ajuste manual y controlado', 'Pensado para FPS mobile'],
      items: [
        { q: '¿Qué gano al crear una cuenta gratis?', a: 'Entras al hub, empiezas por la calculadora y entiendes qué herramienta usar primero.' },
        { q: '¿La calculadora entrega sensibilidad perfecta?', a: 'No existe sensibilidad perfecta universal. GPH entrega una base más inteligente para probar.' },
        { q: '¿Por qué no copiar sensibilidad de un pro?', a: 'Porque control, pantalla, DPI, agarre, Gyro y estilo de pelea cambian el resultado.' },
        { q: '¿GPH accede a mi cuenta del juego?', a: 'No. Game Pro Hub no pide contraseña del juego ni acceso externo.' },
        { q: '¿Sensi-IA cambia mi configuración automáticamente?', a: 'No. Guía pequeños ajustes para que los apliques manualmente.' },
        { q: '¿Coach IA sirve para principiantes?', a: 'Sí. Ayuda a organizar qué entrenar primero y cómo revisar el progreso.' }
      ]
    },
    final: {
      title: 'Calcula tu base antes del próximo entrenamiento.',
      text: 'Crea tu cuenta gratis y empieza con una sensibilidad más consciente.'
    },
    seo: {
      backHome: 'Volver a Game Pro Hub',
      panelTitle: 'Lo que encontrarás aquí',
      panelAriaPrefix: 'Resumen de',
      nextEyebrow: 'Próximo paso',
      nextTitle: 'Convierte el cálculo en rutina de evolución.',
      nextBody: 'Tu cuenta te lleva al hub, donde las herramientas públicas se conectan con historial, rutas y recomendaciones de entrenamiento.',
      haveAccount: 'Ya tengo cuenta'
    }
  }
};

PUBLIC_COPY['pt-BR'].seoRoutes = {
  '/calculadora-sensibilidade-pubg-mobile': {
    kind: 'seo',
    path: '/calculadora-sensibilidade-pubg-mobile',
    title: 'Calculadora de Sensibilidade FPS Mobile (Alpha) | Game Pro Hub',
    ogTitle: 'Calculadora de sensibilidade FPS Mobile (Alpha)',
    description: 'Use a calculadora de sensibilidade FPS Mobile (Alpha) do Game Pro Hub para encontrar uma base segura de câmera, ADS e giroscópio antes de treinar.',
    h1: 'Calculadora de sensibilidade FPS Mobile (Alpha)',
    eyebrow: 'FPS Mobile (Alpha)',
    icon: Target,
    heroText: 'Saia do chute. A calculadora cria uma base de sensibilidade para seu estilo de mira, controle e forma de jogar.',
    sections: [
      { title: 'Para que serve a calculadora FPS Mobile (Alpha)?', body: 'Ela ajuda quando você troca de celular, muda DPI ou sente a mira pesada demais.' },
      { title: 'Como usar o resultado com inteligência', body: 'Use a base em treinos curtos. Depois observe recoil, tracking e encaixe da mira antes de mexer de novo.' },
      { title: 'O que você recebe ao criar conta', body: 'Você pode salvar histórico e combinar a calculadora com Sensi-IA e Coach IA.' }
    ],
    bullets: ['Base por mira e estilo', 'Câmera, ADS e giroscópio', 'Histórico de ajustes', 'Cadastro simples'],
    featureList: ['Calculadora FPS Mobile (Alpha)', 'Ajuste de câmera', 'Ajuste de ADS', 'Ajuste de giroscópio']
  },
  '/calculadora-free-fire': {
    kind: 'seo',
    path: '/calculadora-free-fire',
    title: 'Calculadora FPS Mobile (Beta) de Sensibilidade | Game Pro Hub',
    ogTitle: 'Calculadora FPS Mobile (Beta) para sensibilidade e capa',
    description: 'Calcule uma base de sensibilidade para FPS Mobile (Beta) e ajuste mira, capa e controle com o Game Pro Hub.',
    h1: 'Calculadora FPS Mobile (Beta) de sensibilidade',
    eyebrow: 'FPS Mobile (Beta)',
    icon: Crosshair,
    heroText: 'Ajuste a sensibilidade sem depender de cópia aleatória. Crie uma base para mira, puxada de capa e controle.',
    sections: [
      { title: 'O problema da sensibilidade copiada', body: 'DPI, tela, pegada e taxa de resposta mudam o resultado.' },
      { title: 'Como o GPH ajuda no FPS Mobile (Beta)', body: 'O GPH organiza o ajuste por pontos claros e conecta cada mudança com prática.' },
      { title: 'Do cálculo para o treino', body: 'Use a base calculada como ponto inicial e evolua com trilhas e recomendações.' }
    ],
    bullets: ['Base para mira e capa', 'Ajuste por mira', 'Pensado para mobile', 'Cadastro com acesso grátis'],
    featureList: ['Calculadora FPS Mobile (Beta)', 'Ajuste de mira', 'Controle de capa', 'Treino FPS mobile']
  },
  '/sensi-ia': {
    kind: 'seo',
    path: '/sensi-ia',
    title: 'Sensi-IA: ajuste inteligente de sensibilidade | Game Pro Hub',
    ogTitle: 'Sensi-IA para ajuste fino da sua mira',
    description: 'Conheça a Sensi-IA do Game Pro Hub: ajuste fino para transformar erros de mira em recomendações práticas.',
    h1: 'Sensi-IA para ajuste inteligente de sensibilidade',
    eyebrow: 'Sensi-IA',
    icon: SlidersHorizontal,
    heroText: 'A Sensi-IA ajuda você a sair do ciclo de mudar tudo após cada partida.',
    sections: [
      { title: 'Quando usar a Sensi-IA', body: 'Use quando a mira passa do alvo, perde tracking ou quando a configuração está quase boa.' },
      { title: 'Ajuste fino, não promessa mágica', body: 'O foco é reduzir tentativa e erro com ajustes menores e mais seguros.' },
      { title: 'Integrada ao hub', body: 'Combine calculadora, histórico e recomendações da Sensi-IA em uma jornada única.' }
    ],
    bullets: ['Diagnóstico de mira', 'Sugestões incrementais', 'Histórico de sensibilidade', 'Ajuste com treino'],
    featureList: ['Sensi-IA', 'Ajuste de sensibilidade', 'Diagnóstico de mira', 'Treino guiado']
  },
  '/coach-ia': {
    kind: 'seo',
    path: '/coach-ia',
    title: 'Coach IA para FPS Mobile | Game Pro Hub',
    ogTitle: 'Coach IA para treinar melhor no FPS mobile',
    description: 'O Coach IA do Game Pro Hub transforma erros de gameplay em diagnóstico, trilhas de treino e próximas ações.',
    h1: 'Coach IA para FPS mobile',
    eyebrow: 'Coach IA',
    icon: MessageSquare,
    heroText: 'O Coach IA ajuda você a parar de treinar no escuro.',
    sections: [
      { title: 'Treino com diagnóstico', body: 'O GPH transforma evolução em prioridades: mira, movimentação, recoil, leitura de luta e consistência.' },
      { title: 'Para quem quer rotina', body: 'Útil para jogadores que jogam bastante, mas não conseguem medir se estão melhorando.' },
      { title: 'Comece com uma conta grátis', body: 'Crie sua conta para acessar o hub e testar ferramentas disponíveis.' }
    ],
    bullets: ['Diagnóstico de gameplay', 'Trilhas de treino', 'Próxima ação clara', 'Evolução acompanhada'],
    featureList: ['Coach IA', 'Diagnóstico de gameplay', 'Treino FPS mobile', 'Análise de evolução']
  }
};

PUBLIC_COPY['en-US'].seoRoutes = {
  '/calculadora-sensibilidade-pubg-mobile': {
    ...PUBLIC_COPY['pt-BR'].seoRoutes['/calculadora-sensibilidade-pubg-mobile'],
    title: 'FPS Mobile (Alpha) Sensitivity Calculator | Game Pro Hub',
    ogTitle: 'FPS Mobile (Alpha) sensitivity calculator',
    description: 'Use the Game Pro Hub FPS Mobile (Alpha) sensitivity calculator to build a safer baseline for camera, ADS and Gyro before training.',
    h1: 'FPS Mobile (Alpha) sensitivity calculator',
    heroText: 'Stop guessing. Build a sensitivity baseline for your aiming style, control and way of playing.',
    sections: [
      { title: 'What is the FPS Mobile (Alpha) calculator for?', body: 'It helps when you change phone, adjust DPI or feel your aim is too heavy.' },
      { title: 'How to use the result intelligently', body: 'Use the baseline in short training sessions. Watch recoil, tracking and aim lock before changing again.' },
      { title: 'What you get with an account', body: 'Save history and combine the calculator with Sensi-IA and Coach IA.' }
    ],
    bullets: ['Baseline by aim and style', 'Camera, ADS and Gyro', 'Adjustment history', 'Simple signup']
  },
  '/calculadora-free-fire': {
    ...PUBLIC_COPY['pt-BR'].seoRoutes['/calculadora-free-fire'],
    title: 'FPS Mobile (Beta) Sensitivity Calculator | Game Pro Hub',
    ogTitle: 'FPS Mobile (Beta) calculator for sensitivity and headshots',
    description: 'Calculate a FPS Mobile (Beta) sensitivity baseline and adjust aim, headshot control and consistency with Game Pro Hub.',
    h1: 'FPS Mobile (Beta) sensitivity calculator',
    heroText: 'Adjust sensitivity without random copying. Create a baseline for aim, headshot pull and control.',
    sections: [
      { title: 'The problem with copied sensitivity', body: 'DPI, screen, grip and response rate change the result.' },
      { title: 'How GPH helps in FPS Mobile (Beta)', body: 'GPH organizes adjustments into clear points and connects every change with practice.' },
      { title: 'From calculation to training', body: 'Use the calculated baseline as a starting point and evolve with tracks and recommendations.' }
    ],
    bullets: ['Aim and headshot baseline', 'Scope-based adjustment', 'Built for mobile', 'Free account access']
  },
  '/sensi-ia': {
    ...PUBLIC_COPY['pt-BR'].seoRoutes['/sensi-ia'],
    title: 'Sensi-IA: intelligent sensitivity adjustment | Game Pro Hub',
    ogTitle: 'Sensi-IA for aim fine tuning',
    description: 'Discover Game Pro Hub Sensi-IA: fine tuning to turn aim issues into practical recommendations.',
    h1: 'Sensi-IA for intelligent sensitivity adjustment',
    heroText: 'Sensi-IA helps you stop changing everything after every match.',
    sections: [
      { title: 'When to use Sensi-IA', body: 'Use it when aim overshoots, loses tracking or the setup feels almost right.' },
      { title: 'Fine tuning, not magic', body: 'The focus is reducing trial and error with smaller, safer adjustments.' },
      { title: 'Integrated into the hub', body: 'Combine calculators, history and Sensi-IA recommendations in one journey.' }
    ],
    bullets: ['Aim diagnosis', 'Incremental suggestions', 'Sensitivity history', 'Adjustment with training']
  },
  '/coach-ia': {
    ...PUBLIC_COPY['pt-BR'].seoRoutes['/coach-ia'],
    title: 'Coach IA for Mobile FPS | Game Pro Hub',
    ogTitle: 'Coach IA for better mobile FPS training',
    description: 'Game Pro Hub Coach IA turns gameplay mistakes into diagnosis, training paths and next actions.',
    h1: 'Coach IA for mobile FPS',
    heroText: 'Coach IA helps you stop training blindly.',
    sections: [
      { title: 'Training with diagnosis', body: 'GPH turns progress into priorities: aim, movement, recoil, fight reading and consistency.' },
      { title: 'For players who want routine', body: 'Useful for players who play a lot but cannot measure if they are improving.' },
      { title: 'Start with a free account', body: 'Create your account to access the hub and test available tools.' }
    ],
    bullets: ['Gameplay diagnosis', 'Training paths', 'Clear next action', 'Tracked progress']
  }
};

PUBLIC_COPY['es-ES'].seoRoutes = {
  '/calculadora-sensibilidade-pubg-mobile': {
    ...PUBLIC_COPY['pt-BR'].seoRoutes['/calculadora-sensibilidade-pubg-mobile'],
    title: 'Calculadora de sensibilidad FPS Mobile (Alpha) | Game Pro Hub',
    ogTitle: 'Calculadora de sensibilidad FPS Mobile (Alpha)',
    description: 'Usa la calculadora de sensibilidad FPS Mobile (Alpha) de Game Pro Hub para crear una base segura de cámara, ADS y Gyro antes de entrenar.',
    h1: 'Calculadora de sensibilidad FPS Mobile (Alpha)',
    heroText: 'Deja de adivinar. Crea una base de sensibilidad para tu estilo de mira, control y forma de jugar.',
    sections: [
      { title: '¿Para qué sirve la calculadora FPS Mobile (Alpha)?', body: 'Ayuda cuando cambias de celular, ajustas DPI o sientes la mira demasiado pesada.' },
      { title: 'Cómo usar el resultado con inteligencia', body: 'Usa la base en entrenos cortos. Observa recoil, tracking y encaje de mira antes de cambiar de nuevo.' },
      { title: 'Qué recibes al crear cuenta', body: 'Puedes guardar historial y combinar la calculadora con Sensi-IA y Coach IA.' }
    ],
    bullets: ['Base por mira y estilo', 'Cámara, ADS y Gyro', 'Historial de ajustes', 'Registro simple']
  },
  '/calculadora-free-fire': {
    ...PUBLIC_COPY['pt-BR'].seoRoutes['/calculadora-free-fire'],
    title: 'Calculadora FPS Mobile (Beta) de sensibilidad | Game Pro Hub',
    ogTitle: 'Calculadora FPS Mobile (Beta) para sensibilidad y headshot',
    description: 'Calcula una base de sensibilidad para FPS Mobile (Beta) y ajusta mira, headshot y control con Game Pro Hub.',
    h1: 'Calculadora FPS Mobile (Beta) de sensibilidad',
    heroText: 'Ajusta la sensibilidad sin copiar al azar. Crea una base para mira, headshot y control.',
    sections: [
      { title: 'El problema de la sensibilidad copiada', body: 'DPI, pantalla, agarre y tasa de respuesta cambian el resultado.' },
      { title: 'Cómo GPH ayuda en FPS Mobile (Beta)', body: 'GPH organiza ajustes en puntos claros y conecta cada cambio con práctica.' },
      { title: 'Del cálculo al entreno', body: 'Usa la base calculada como punto inicial y evoluciona con rutas y recomendaciones.' }
    ],
    bullets: ['Base para mira y headshot', 'Ajuste por mira', 'Pensado para mobile', 'Acceso con cuenta gratis']
  },
  '/sensi-ia': {
    ...PUBLIC_COPY['pt-BR'].seoRoutes['/sensi-ia'],
    title: 'Sensi-IA: ajuste inteligente de sensibilidad | Game Pro Hub',
    ogTitle: 'Sensi-IA para ajuste fino de tu mira',
    description: 'Conoce Sensi-IA de Game Pro Hub: ajuste fino para convertir errores de mira en recomendaciones prácticas.',
    h1: 'Sensi-IA para ajuste inteligente de sensibilidad',
    heroText: 'Sensi-IA te ayuda a dejar el ciclo de cambiar todo después de cada partida.',
    sections: [
      { title: 'Cuándo usar Sensi-IA', body: 'Úsala cuando la mira pasa del objetivo, pierde tracking o la configuración está casi bien.' },
      { title: 'Ajuste fino, no magia', body: 'El foco es reducir prueba y error con ajustes más pequeños y seguros.' },
      { title: 'Integrada al hub', body: 'Combina calculadora, historial y recomendaciones de Sensi-IA en una sola jornada.' }
    ],
    bullets: ['Diagnóstico de mira', 'Sugerencias incrementales', 'Historial de sensibilidad', 'Ajuste con entrenamiento']
  },
  '/coach-ia': {
    ...PUBLIC_COPY['pt-BR'].seoRoutes['/coach-ia'],
    title: 'Coach IA para FPS Mobile | Game Pro Hub',
    ogTitle: 'Coach IA para entrenar mejor en FPS mobile',
    description: 'Coach IA de Game Pro Hub convierte errores de gameplay en diagnóstico, rutas de entrenamiento y próximas acciones.',
    h1: 'Coach IA para FPS mobile',
    heroText: 'Coach IA te ayuda a dejar de entrenar a ciegas.',
    sections: [
      { title: 'Entrenamiento con diagnóstico', body: 'GPH convierte evolución en prioridades: mira, movimiento, recoil, lectura de pelea y consistencia.' },
      { title: 'Para quien quiere rutina', body: 'Útil para jugadores que juegan bastante, pero no consiguen medir si mejoran.' },
      { title: 'Empieza con una cuenta gratis', body: 'Crea tu cuenta para acceder al hub y probar herramientas disponibles.' }
    ],
    bullets: ['Diagnóstico de gameplay', 'Rutas de entrenamiento', 'Próxima acción clara', 'Evolución acompañada']
  }
};

const LANDING_ENHANCEMENTS = {
  'pt-BR': {
    landingRoute: {
      title: 'Game Pro Hub - treino, mira e IA para FPS mobile',
      ogTitle: 'Game Pro Hub: pare de ajustar no escuro',
      description: 'Diagnostique erros da mira, crie uma base segura e treine com metodo em um app premium para FPS mobile.'
    },
    proofStrip: ['FPS Mobile (Alpha)', 'FPS Mobile (Beta)', 'FPS Mobile (Gamma)', 'Calculadora FPS Mobile (Alpha)', 'Calculadora FPS Mobile (Beta)', 'Sensi-IA', 'Mi Coach', 'Mi Análise'],
    community: {
      eyebrow: 'Sistema de evolução',
      title: 'Um hub para calibrar, diagnosticar e treinar.',
      body: 'O GPH conecta cálculo de sensibilidade, diagnóstico de mira e treino guiado em uma experiência única para FPS mobile.',
      signals: [
        { value: 'FPS Mobile (Alpha)', label: 'calculadora, Sensi-IA e treinos', detail: 'Base de câmera, ADS, Gyro, recoil e rotina de validação.' },
        { value: 'FF', label: 'calculadora, Sensi-IA e treinos', detail: 'Ajuste de mira, controle de capa e rotina curta para consistência.' },
        { value: 'FPS Mobile (Gamma)', label: 'Sensi-IA, Mi Coach e Mi Análise', detail: 'Diagnóstico e treino sem prometer calculadora específica de FPS Mobile (Gamma).' },
        { value: '0', label: 'senha do jogo solicitada', detail: 'Você aplica ajustes manualmente e mantém sua conta protegida.' }
      ]
    },
    pain: {
      eyebrow: 'O problema real',
      title: 'Você não erra só por falta de treino. Erra por ajustar no escuro.',
      body: 'Copiar sensibilidade de outra pessoa não considera sua mira, seu FPS, seu controle, seu jogo e o erro real do seu spray.'
    },
    solution: {
      eyebrow: 'Método GPH',
      title: 'Calcule. Diagnostique. Treine.',
      body: 'Uma sequência simples para sair do achismo e validar cada ajuste antes de mudar tudo outra vez.',
      items: [
        { icon: Target, title: 'Calcule uma base segura', text: 'Use calculadoras para criar ponto de partida em FPS Mobile (Alpha) e FPS Mobile (Beta).' },
        { icon: SlidersHorizontal, title: 'Diagnostique o erro da mira', text: 'Transforme mira subindo, spray abrindo ou tracking perdido em ajuste claro.' },
        { icon: Gamepad2, title: 'Treine com foco', text: 'Receba uma rotina curta para validar o ajuste e evoluir com método.' }
      ]
    },
    tools: {
      eyebrow: 'Sistema conectado',
      title: 'Tudo conectado em um sistema de evolução.',
      body: 'Calculadoras específicas, Sensi-IA, Mi Coach e Mi Análise trabalham como uma jornada, não como ferramentas soltas.',
      cards: [
        { id: 'calculadora-pubg', icon: Crosshair, title: 'Calculadora FPS Mobile (Alpha)', text: 'Base para câmera, ADS e giroscópio antes do treino.', href: '/login' },
        { id: 'calculadora-ff', icon: Flame, title: 'Calculadora FPS Mobile (Beta)', text: 'Ajuste de sensibilidade para controle, capa e mira.', href: '/login' },
        { id: 'sensi-ia', icon: SlidersHorizontal, title: 'Sensi-IA', text: 'Diagnóstico de sintomas como recoil abrindo, mira subindo e tracking instável.', href: '/sensi-ia' },
        { id: 'coach-ia', icon: Gamepad2, title: 'Mi Coach', text: 'Treinos guiados para FPS Mobile (Alpha), FPS Mobile (Beta) e FPS Mobile (Gamma).', href: '/coach-ia' }
      ]
    },
    games: {
      eyebrow: 'Jogos suportados',
      title: 'Feito para FPS Mobile (Alpha), FPS Mobile (Beta) e FPS Mobile (Gamma).',
      body: 'Cada jogo entra no sistema com o recurso certo. FPS Mobile (Gamma) aparece no treino e diagnóstico, sem promessa de calculadora FPS Mobile (Gamma).',
      items: [
        { title: 'FPS Mobile (Alpha)', text: 'Calculadora + treinos + Sensi-IA.', tags: ['Câmera', 'ADS', 'Gyro', 'Recoil'] },
        { title: 'FPS Mobile (Beta)', text: 'Calculadora + treinos + Sensi-IA.', tags: ['Mira', 'Capa', 'DPI', 'Rotina'] },
        { title: 'FPS Mobile (Gamma)', text: 'Sensi-IA + Mi Coach + Mi Análise.', tags: ['Tracking', 'Spray', 'Treino', 'Análise'] }
      ]
    },
    useCases: ['Minha mira sobe', 'Meu spray abre', 'Erro nas primeiras balas', 'Troquei de celular', 'Quero ajustar giroscópio', 'Quero melhorar movimentação', 'Quero treinar ranked', 'Quero treinar FPS Mobile (Gamma)', 'Quero melhorar no FPS Mobile (Beta)', 'Quero parar de copiar sensi', 'Quero uma rotina clara', 'Perco tracking no close'],
    plans: {
      title: 'Comece grátis. Evolua quando fizer sentido.',
      body: 'Planos em BRL, checkout seguro e acesso conforme o produto escolhido dentro do app.',
      items: [
        { badge: 'Free', name: 'Conta Free', price: 'R$ 0', text: 'Entre no hub, teste a jornada e descubra o melhor primeiro passo.', features: ['Acesso inicial', 'Base de evolução', 'Upgrade quando fizer sentido'] },
        { badge: 'Flexível', name: 'Apoiador Mensal', price: 'R$ 29,99/mês', text: 'Para quem quer desbloquear o GPH completo sem compromisso.', features: ['Coach completa', 'Sensi-IA', 'Mi Análise', 'Calculadoras'] },
        { badge: 'Melhor escolha', name: 'Apoiador Anual', price: 'R$ 289,90/ano', text: 'Pague uma vez e evolua durante o ano todo. Sai por menos de R$25 por mês.', features: ['Tudo do mensal', '12 meses de Pro', 'Melhor custo-benefício'], featured: true },
        { badge: 'Pagamento único', name: 'Apoiador Vitalício', price: 'R$ 286,90', text: 'Pague uma única vez e nunca mais jogue como noob no improviso.', features: ['Acesso Pro vitalício', 'Sem renovação mensal', 'Recursos Pro atuais e melhorias futuras', 'Sujeito a limites justos de uso'] }
      ]
    },
    calculatorOffers: {
      eyebrow: 'Compras avulsas',
      title: 'Calculadoras vitalícias em um campo próprio.',
      body: 'Prefere comprar só a calculadora do seu jogo? FPS Mobile (Alpha) e FPS Mobile (Beta) continuam separados do plano Pro.',
      cta: 'Abrir compra',
      items: [
        { id: 'pubg', badge: 'FPS Mobile (Alpha)', name: 'Calculadora FPS Mobile (Alpha)', price: 'Incluso no Pro', text: 'Crie base de câmera, ADS e Gyro com mais controle. As calculadoras agora fazem parte do Pro; quem comprou vitalício antes mantém o acesso.', image: '/assets/calculator/pubg-calculator-hero.webp', features: ['Incluso no Pro', 'Camera / ADS / Gyro', 'Base reutilizável'] },
        { id: 'freefire', badge: 'FPS Mobile (Beta)', name: 'Calculadora FPS Mobile (Beta)', price: 'R$ 15,99', text: 'Acesso vitalício para ajustar mira, capa e rotina sem depender de chute.', image: '/assets/calculator/free-fire-calculator-hero.svg', features: ['Uso vitalício', 'Mira e capa', 'Ajuste por rotina'] }
      ]
    },
    faq: {
      items: [
        { q: 'O Game Pro Hub funciona para FPS Mobile (Alpha)?', a: 'Sim. O GPH tem calculadora FPS Mobile (Alpha), Sensi-IA, Mi Coach e treinos para ajudar com sensibilidade, recoil e rotina.' },
        { q: 'O Game Pro Hub funciona para FPS Mobile (Beta)?', a: 'Sim. FPS Mobile (Beta) tem calculadora própria, treinos, diagnóstico de mira e suporte da Sensi-IA.' },
        { q: 'O Game Pro Hub funciona para FPS Mobile (Gamma)?', a: 'Sim. FPS Mobile (Gamma) é suportado por Sensi-IA, Mi Coach e Mi Análise. Não prometemos calculadora FPS Mobile (Gamma) específica.' },
        { q: 'O que é a Sensi-IA?', a: 'É a camada de diagnóstico que transforma sintomas da mira em recomendações de ajuste e treino.' },
        { q: 'Preciso enviar vídeo?', a: 'Não. Você pode começar por relato. O vídeo é opcional quando quiser uma análise com mais contexto.' },
        { q: 'A calculadora funciona para qualquer celular?', a: 'Ela cria uma base para testar. Tela, FPS, DPI e controle ainda precisam ser validados na prática.' },
        { q: 'Por que não copiar sensibilidade de pro player?', a: 'Porque mão, tela, FPS, giroscópio, estilo de luta e rotina mudam o resultado.' },
        { q: 'Qual a diferença entre Free, Pro e Vitalício?', a: 'Free permite começar. Pro libera recursos de evolução. Vitalício libera uma calculadora específica sem mensalidade.' },
        { q: 'O Pro Anual é vitalício?', a: 'Não. Quando existir na oferta, Pro Anual será acesso por período definido, diferente das compras vitalícias.' },
        { q: 'Posso usar no celular?', a: 'Sim. O Game Pro Hub é pensado para uso mobile e PWA.' }
      ]
    },
    final: {
      title: 'Entre com uma base. Treine com um método.',
      text: 'Comece agora e transforme sensibilidade, mira e treino em uma rotina mensurável.'
    }
  },
  'en-US': {
    landingRoute: {
      title: 'Game Pro Hub - training, aim and AI for mobile FPS',
      ogTitle: 'Game Pro Hub: stop adjusting blindly',
      description: 'Diagnose aim mistakes, build a safer baseline and train with method in a premium mobile FPS app.'
    },
    proofStrip: ['FPS Mobile (Alpha)', 'FPS Mobile (Beta)', 'FPS Mobile (Gamma)', 'FPS Mobile (Alpha) Calculator', 'FPS Mobile (Beta) Calculator', 'Sensi-IA', 'Mi Coach', 'Mi Analysis'],
    community: {
      eyebrow: 'Improvement system',
      title: 'One hub to calibrate, diagnose and train.',
      body: 'GPH connects sensitivity calculation, aim diagnosis and guided training into one mobile FPS journey.',
      signals: [
        { value: 'FPS Mobile (Alpha)', label: 'calculator, Sensi-IA and drills', detail: 'Camera, ADS, Gyro, recoil and validation routines.' },
        { value: 'FF', label: 'calculator, Sensi-IA and drills', detail: 'Aim control, headshot pull and short consistency routines.' },
        { value: 'FPS Mobile (Gamma)', label: 'Sensi-IA, Mi Coach and Mi Analysis', detail: 'Diagnosis and training without promising a dedicated FPS Mobile (Gamma) calculator.' },
        { value: '0', label: 'game passwords requested', detail: 'You apply changes manually and keep your account protected.' }
      ]
    },
    pain: {
      eyebrow: 'The real problem',
      title: 'You do not miss only because of practice. You miss because you adjust blindly.',
      body: 'Copying someone else’s sensitivity ignores your aim, FPS, control style, game and the real error in your spray.'
    },
    solution: {
      eyebrow: 'GPH method',
      title: 'Calculate. Diagnose. Train.',
      body: 'A simple sequence to leave guesswork behind and validate each change before changing everything again.',
      items: [
        { icon: Target, title: 'Calculate a safe baseline', text: 'Use calculators to create a starting point in FPS Mobile (Alpha) and FPS Mobile (Beta).' },
        { icon: SlidersHorizontal, title: 'Diagnose the aim error', text: 'Turn rising aim, open spray or lost tracking into a clear adjustment.' },
        { icon: Gamepad2, title: 'Train with focus', text: 'Get a short routine to validate the setup and improve with method.' }
      ]
    },
    tools: {
      eyebrow: 'Connected system',
      title: 'Everything connected in one improvement system.',
      body: 'Dedicated calculators, Sensi-IA, Mi Coach and Mi Analysis work as a journey, not isolated tools.',
      cards: [
        { id: 'calculadora-pubg', icon: Crosshair, title: 'FPS Mobile (Alpha) Calculator', text: 'Baseline for camera, ADS and Gyro before training.', href: '/login' },
        { id: 'calculadora-ff', icon: Flame, title: 'FPS Mobile (Beta) Calculator', text: 'Sensitivity adjustment for aim, headshot control and routine.', href: '/login' },
        { id: 'sensi-ia', icon: SlidersHorizontal, title: 'Sensi-IA', text: 'Diagnosis for recoil opening, rising aim and unstable tracking.', href: '/sensi-ia' },
        { id: 'coach-ia', icon: Gamepad2, title: 'Mi Coach', text: 'Guided drills for FPS Mobile (Alpha), FPS Mobile (Beta) and FPS Mobile (Gamma).', href: '/coach-ia' }
      ]
    },
    games: {
      eyebrow: 'Supported games',
      title: 'Built for FPS Mobile (Alpha), FPS Mobile (Beta) and FPS Mobile (Gamma).',
      body: 'Each game enters the system with the right resource. FPS Mobile (Gamma) appears in training and diagnosis, without promising a FPS Mobile (Gamma) calculator.',
      items: [
        { title: 'FPS Mobile (Alpha)', text: 'Calculator + drills + Sensi-IA.', tags: ['Camera', 'ADS', 'Gyro', 'Recoil'] },
        { title: 'FPS Mobile (Beta)', text: 'Calculator + drills + Sensi-IA.', tags: ['Aim', 'Headshot', 'DPI', 'Routine'] },
        { title: 'FPS Mobile (Gamma)', text: 'Sensi-IA + Mi Coach + Mi Analysis.', tags: ['Tracking', 'Spray', 'Training', 'Analysis'] }
      ]
    },
    useCases: ['My aim rises', 'My spray opens', 'First bullets miss', 'I changed phone', 'Gyro adjustment', 'Movement improvement', 'Ranked training', 'FPS Mobile (Gamma) training', 'FPS Mobile (Beta) improvement', 'Stop copying sensitivity', 'Clear routine', 'Close-range tracking'],
    plans: {
      title: 'Start free. Upgrade when it makes sense.',
      body: 'BRL pricing, secure checkout and access based on the product you choose inside the app.',
      items: [
        { badge: 'Free', name: 'Free account', price: 'R$ 0', text: 'Enter the hub, test the journey and discover the right first step.', features: ['Initial access', 'Progress baseline', 'Upgrade when ready'] },
        { badge: 'Flexible', name: 'Monthly Supporter', price: 'R$ 29,99/month', text: 'Unlock the full GPH without a long commitment.', features: ['Full Coach', 'Sensi-IA', 'Mi Analysis', 'Calculators'] },
        { badge: 'Best choice', name: 'Annual Supporter', price: 'R$ 289,90/year', text: 'Pay once and improve throughout the whole year. Less than R$25 per month.', features: ['Everything monthly', '12 months of Pro', 'Best value'], featured: true },
        { badge: 'One-time payment', name: 'Lifetime Supporter', price: 'R$ 286,90', text: 'Pay once and stop training by random improvisation.', features: ['Lifetime Pro access', 'No monthly renewal', 'Current Pro resources and future improvements', 'Subject to fair-use limits'] }
      ]
    },
    calculatorOffers: {
      eyebrow: 'One-time purchases',
      title: 'Lifetime calculators in a dedicated section.',
      body: 'Want only the calculator for your game? FPS Mobile (Alpha) and FPS Mobile (Beta) stay separate from the Pro plan.',
      cta: 'Open purchase',
      items: [
        { id: 'pubg', badge: 'FPS Mobile (Alpha)', name: 'FPS Mobile (Alpha) Calculator', price: 'Included in Pro', text: 'Build camera, ADS and Gyro baselines with more control. Calculators are now part of Pro; users who bought lifetime before keep access.', image: '/assets/calculator/pubg-calculator-hero.webp', features: ['Included in Pro', 'Camera / ADS / Gyro', 'Reusable baseline'] },
        { id: 'freefire', badge: 'FPS Mobile (Beta)', name: 'FPS Mobile (Beta) Calculator', price: 'R$ 15,99', text: 'Lifetime access to tune aim, headshot pull and routine without guessing.', image: '/assets/calculator/free-fire-calculator-hero.svg', features: ['Lifetime use', 'Aim and headshot', 'Routine-based tuning'] }
      ]
    },
    faq: {
      items: [
        { q: 'Does Game Pro Hub work for FPS Mobile (Alpha)?', a: 'Yes. GPH includes a FPS Mobile (Alpha) calculator, Sensi-IA, Mi Coach and drills for sensitivity, recoil and routine.' },
        { q: 'Does Game Pro Hub work for FPS Mobile (Beta)?', a: 'Yes. FPS Mobile (Beta) has its own calculator, drills, aim diagnosis and Sensi-IA support.' },
        { q: 'Does Game Pro Hub work for FPS Mobile (Gamma)?', a: 'Yes. FPS Mobile (Gamma) is supported by Sensi-IA, Mi Coach and Mi Analysis. We do not promise a dedicated FPS Mobile (Gamma) calculator.' },
        { q: 'What is Sensi-IA?', a: 'It is the diagnosis layer that turns aim symptoms into adjustment and training recommendations.' },
        { q: 'Do I need to upload video?', a: 'No. You can start with a report. Video is optional when you want more context.' },
        { q: 'Does the calculator work for any phone?', a: 'It creates a baseline to test. Screen, FPS, DPI and control still need practical validation.' },
        { q: 'Why not copy a pro player sensitivity?', a: 'Because hand control, screen, FPS, Gyro, fight style and routine change the result.' },
        { q: 'What is the difference between Free, Pro and Lifetime?', a: 'Free lets you start. Pro unlocks improvement resources. Lifetime unlocks one specific calculator without a monthly fee.' },
        { q: 'Is Annual Pro lifetime?', a: 'No. If offered, Annual Pro is time-limited access, unlike lifetime purchases.' },
        { q: 'Can I use it on mobile?', a: 'Yes. Game Pro Hub is designed for mobile and PWA usage.' }
      ]
    },
    final: {
      title: 'Enter with a baseline. Train with a method.',
      text: 'Start now and turn sensitivity, aim and training into a measurable routine.'
    }
  },
  'es-ES': {
    landingRoute: {
      title: 'Game Pro Hub - entrenamiento, mira e IA para FPS mobile',
      ogTitle: 'Game Pro Hub: deja de ajustar a ciegas',
      description: 'Diagnostica errores de mira, crea una base segura y entrena con metodo en una app premium para FPS mobile.'
    },
    proofStrip: ['FPS Mobile (Alpha)', 'FPS Mobile (Beta)', 'FPS Mobile (Gamma)', 'Calculadora FPS Mobile (Alpha)', 'Calculadora FPS Mobile (Beta)', 'Sensi-IA', 'Mi Coach', 'Mi Análisis'],
    community: {
      eyebrow: 'Sistema de evolución',
      title: 'Un hub para calibrar, diagnosticar y entrenar.',
      body: 'GPH conecta cálculo de sensibilidad, diagnóstico de mira y entrenamiento guiado en una experiencia para FPS mobile.',
      signals: [
        { value: 'FPS Mobile (Alpha)', label: 'calculadora, Sensi-IA y entrenos', detail: 'Cámara, ADS, Gyro, recoil y rutina de validación.' },
        { value: 'FF', label: 'calculadora, Sensi-IA y entrenos', detail: 'Ajuste de mira, control de headshot y rutina corta.' },
        { value: 'FPS Mobile (Gamma)', label: 'Sensi-IA, Mi Coach y Mi Análisis', detail: 'Diagnóstico y entrenamiento sin prometer calculadora FPS Mobile (Gamma) específica.' },
        { value: '0', label: 'contraseñas del juego solicitadas', detail: 'Aplicas ajustes manualmente y mantienes tu cuenta protegida.' }
      ]
    },
    pain: {
      eyebrow: 'El problema real',
      title: 'No fallas solo por falta de entrenamiento. Fallas por ajustar a ciegas.',
      body: 'Copiar sensibilidad de otra persona no considera tu mira, FPS, control, juego ni el error real del spray.'
    },
    solution: {
      eyebrow: 'Método GPH',
      title: 'Calcula. Diagnostica. Entrena.',
      body: 'Una secuencia simple para salir de la adivinanza y validar cada ajuste antes de cambiar todo otra vez.',
      items: [
        { icon: Target, title: 'Calcula una base segura', text: 'Usa calculadoras para crear un punto inicial en FPS Mobile (Alpha) y FPS Mobile (Beta).' },
        { icon: SlidersHorizontal, title: 'Diagnostica el error de mira', text: 'Convierte mira que sube, spray abierto o tracking perdido en ajuste claro.' },
        { icon: Gamepad2, title: 'Entrena con foco', text: 'Recibe una rutina corta para validar el ajuste y evolucionar con método.' }
      ]
    },
    tools: {
      eyebrow: 'Sistema conectado',
      title: 'Todo conectado en un sistema de evolución.',
      body: 'Calculadoras específicas, Sensi-IA, Mi Coach y Mi Análisis funcionan como una jornada, no como herramientas sueltas.',
      cards: [
        { id: 'calculadora-pubg', icon: Crosshair, title: 'Calculadora FPS Mobile (Alpha)', text: 'Base para cámara, ADS y giroscopio antes del entreno.', href: '/login' },
        { id: 'calculadora-ff', icon: Flame, title: 'Calculadora FPS Mobile (Beta)', text: 'Ajuste de sensibilidad para mira, headshot y rutina.', href: '/login' },
        { id: 'sensi-ia', icon: SlidersHorizontal, title: 'Sensi-IA', text: 'Diagnóstico de recoil abierto, mira subiendo y tracking inestable.', href: '/sensi-ia' },
        { id: 'coach-ia', icon: Gamepad2, title: 'Mi Coach', text: 'Entrenos guiados para FPS Mobile (Alpha), FPS Mobile (Beta) y FPS Mobile (Gamma).', href: '/coach-ia' }
      ]
    },
    games: {
      eyebrow: 'Juegos compatibles',
      title: 'Hecho para FPS Mobile (Alpha), FPS Mobile (Beta) y FPS Mobile (Gamma).',
      body: 'Cada juego entra al sistema con el recurso correcto. FPS Mobile (Gamma) aparece en entrenamiento y diagnóstico, sin prometer calculadora FPS Mobile (Gamma).',
      items: [
        { title: 'FPS Mobile (Alpha)', text: 'Calculadora + entrenos + Sensi-IA.', tags: ['Cámara', 'ADS', 'Gyro', 'Recoil'] },
        { title: 'FPS Mobile (Beta)', text: 'Calculadora + entrenos + Sensi-IA.', tags: ['Mira', 'Headshot', 'DPI', 'Rutina'] },
        { title: 'FPS Mobile (Gamma)', text: 'Sensi-IA + Mi Coach + Mi Análisis.', tags: ['Tracking', 'Spray', 'Entreno', 'Análisis'] }
      ]
    },
    useCases: ['Mi mira sube', 'Mi spray se abre', 'Fallo las primeras balas', 'Cambié de celular', 'Ajustar giroscopio', 'Mejorar movimiento', 'Entrenar ranked', 'Entrenar FPS Mobile (Gamma)', 'Mejorar en FPS Mobile (Beta)', 'Dejar de copiar sensi', 'Rutina clara', 'Tracking en close'],
    plans: {
      title: 'Empieza gratis. Evoluciona cuando tenga sentido.',
      body: 'Precios en BRL, checkout seguro y acceso según el producto elegido dentro de la app.',
      items: [
        { badge: 'Free', name: 'Cuenta Free', price: 'R$ 0', text: 'Entra al hub, prueba la jornada y descubre el mejor primer paso.', features: ['Acceso inicial', 'Base de evolución', 'Upgrade cuando tenga sentido'] },
        { badge: 'Flexible', name: 'Apoiador Mensual', price: 'R$ 29,99/mes', text: 'Para desbloquear GPH completo sin compromiso largo.', features: ['Coach completo', 'Sensi-IA', 'Mi Análisis', 'Calculadoras'] },
        { badge: 'Mejor opción', name: 'Apoiador Anual', price: 'R$ 289,90/año', text: 'Paga una vez y evoluciona todo el año. Menos de R$25 por mes.', features: ['Todo del mensual', '12 meses de Pro', 'Mejor costo-beneficio'], featured: true },
        { badge: 'Pago único', name: 'Apoiador Vitalicio', price: 'R$ 286,90', text: 'Paga una vez y deja de entrenar al azar.', features: ['Acceso Pro vitalicio', 'Sin renovación mensual', 'Recursos Pro actuales y mejoras futuras', 'Sujeto a limites justos de uso'] }
      ]
    },
    calculatorOffers: {
      eyebrow: 'Compras individuales',
      title: 'Calculadoras vitalicias en una sección propia.',
      body: '¿Prefieres comprar solo la calculadora de tu juego? FPS Mobile (Alpha) y FPS Mobile (Beta) quedan separados del plan Pro.',
      cta: 'Abrir compra',
      items: [
        { id: 'pubg', badge: 'FPS Mobile (Alpha)', name: 'Calculadora FPS Mobile (Alpha)', price: 'Incluido en Pro', text: 'Crea bases de cámara, ADS y Gyro con más control. Las calculadoras ahora son parte del Pro; quien compró vitalicio antes mantiene el acceso.', image: '/assets/calculator/pubg-calculator-hero.webp', features: ['Incluido en Pro', 'Cámara / ADS / Gyro', 'Base reutilizable'] },
        { id: 'freefire', badge: 'FPS Mobile (Beta)', name: 'Calculadora FPS Mobile (Beta)', price: 'R$ 15,99', text: 'Acceso vitalicio para ajustar mira, headshot y rutina sin adivinar.', image: '/assets/calculator/free-fire-calculator-hero.svg', features: ['Uso vitalicio', 'Mira y headshot', 'Ajuste por rutina'] }
      ]
    },
    faq: {
      items: [
        { q: '¿Game Pro Hub funciona para FPS Mobile (Alpha)?', a: 'Sí. GPH incluye calculadora FPS Mobile (Alpha), Sensi-IA, Mi Coach y entrenos para sensibilidad, recoil y rutina.' },
        { q: '¿Game Pro Hub funciona para FPS Mobile (Beta)?', a: 'Sí. FPS Mobile (Beta) tiene calculadora propia, entrenos, diagnóstico de mira y soporte de Sensi-IA.' },
        { q: '¿Game Pro Hub funciona para FPS Mobile (Gamma)?', a: 'Sí. FPS Mobile (Gamma) está soportado por Sensi-IA, Mi Coach y Mi Análisis. No prometemos calculadora FPS Mobile (Gamma) dedicada.' },
        { q: '¿Qué es Sensi-IA?', a: 'Es la capa de diagnóstico que convierte síntomas de mira en recomendaciones de ajuste y entrenamiento.' },
        { q: '¿Necesito enviar video?', a: 'No. Puedes empezar con relato. El video es opcional cuando quieres más contexto.' },
        { q: '¿La calculadora funciona para cualquier celular?', a: 'Crea una base para probar. Pantalla, FPS, DPI y control aún deben validarse en práctica.' },
        { q: '¿Por qué no copiar sensibilidad de un pro player?', a: 'Porque mano, pantalla, FPS, giroscopio, estilo de pelea y rutina cambian el resultado.' },
        { q: '¿Cuál es la diferencia entre Free, Pro y Vitalicio?', a: 'Free permite empezar. Pro libera recursos de evolución. Vitalicio libera una calculadora específica sin mensualidad.' },
        { q: '¿El Pro Anual es vitalicio?', a: 'No. Si existe en la oferta, Pro Anual es acceso por tiempo definido, distinto de compras vitalicias.' },
        { q: '¿Puedo usarlo en celular?', a: 'Sí. Game Pro Hub está pensado para mobile y PWA.' }
      ]
    },
    final: {
      title: 'Entra con una base. Entrena con un método.',
      text: 'Empieza ahora y convierte sensibilidad, mira y entrenamiento en una rutina medible.'
    }
  }
};

const mergeLandingCopy = (base, enhancement = {}) => ({
  ...base,
  landingRoute: { ...base.landingRoute, ...(enhancement.landingRoute || {}) },
  community: { ...base.community, ...(enhancement.community || {}) },
  pain: { ...base.pain, ...(enhancement.pain || {}) },
  solution: { ...base.solution, ...(enhancement.solution || {}) },
  tools: { ...base.tools, ...(enhancement.tools || {}) },
  games: enhancement.games || base.games,
  calculatorOffers: enhancement.calculatorOffers || base.calculatorOffers,
  proofStrip: enhancement.proofStrip || base.proofStrip,
  useCases: enhancement.useCases || base.useCases,
  plans: { ...base.plans, ...(enhancement.plans || {}) },
  faq: {
    ...base.faq,
    ...(enhancement.faq || {}),
    items: enhancement.faq?.items || base.faq.items
  },
  final: { ...base.final, ...(enhancement.final || {}) }
});

const getPublicCopy = (language) => {
  const locale = canonicalLanguage(language);
  return mergeLandingCopy(PUBLIC_COPY[locale] || PUBLIC_COPY['pt-BR'], LANDING_ENHANCEMENTS[locale]);
};

const getLocalizedRoute = (route, language) => {
  const copy = getPublicCopy(language);
  if (!route || route.kind === 'landing' || route.path === '/') return copy.landingRoute;
  if (route.kind === 'login' || route.path === '/login') return copy.loginRoute;
  return copy.seoRoutes[route.path] || route;
};

const findPublicSearchTarget = (query, language) => {
  const copy = getPublicCopy(language);
  const normalized = String(query || '').trim().toLowerCase();
  if (!normalized) return null;
  return copy.nav.find((item) => (
    item.label.toLowerCase().includes(normalized)
    || item.keywords.some((keyword) => keyword.includes(normalized) || normalized.includes(keyword))
  ));
};

const setMeta = (selector, attr, value) => {
  if (typeof document === 'undefined') return;
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    const nameMatch = selector.match(/name="([^"]+)"/);
    const propertyMatch = selector.match(/property="([^"]+)"/);
    if (nameMatch) element.setAttribute('name', nameMatch[1]);
    if (propertyMatch) element.setAttribute('property', propertyMatch[1]);
    document.head.appendChild(element);
  }
  element.setAttribute(attr, value);
};

const setLink = (rel, href) => {
  if (typeof document === 'undefined') return;
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

export const applyPublicSeoMeta = (route, language = 'pt-BR') => {
  if (typeof document === 'undefined' || !route) return;

  const localizedRoute = getLocalizedRoute(route, language);
  const locale = canonicalLanguage(language) === 'pt-BR' ? 'pt_BR' : canonicalLanguage(language).replace('-', '_');
  const url = `${SITE_URL}${localizedRoute.path}`;
  document.title = localizedRoute.title;
  setMeta('meta[name="description"]', 'content', localizedRoute.description);
  setMeta('meta[name="robots"]', 'content', localizedRoute.robots || 'index, follow');
  setLink('canonical', url);
  setMeta('meta[property="og:type"]', 'content', 'website');
  setMeta('meta[property="og:locale"]', 'content', locale);
  setMeta('meta[property="og:site_name"]', 'content', 'Game Pro Hub');
  setMeta('meta[property="og:title"]', 'content', localizedRoute.ogTitle || localizedRoute.title);
  setMeta('meta[property="og:description"]', 'content', localizedRoute.description);
  setMeta('meta[property="og:url"]', 'content', url);
  setMeta('meta[property="og:image"]', 'content', localizedRoute.image || BRAND_IMAGE);
  setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
  setMeta('meta[name="twitter:title"]', 'content', localizedRoute.ogTitle || localizedRoute.title);
  setMeta('meta[name="twitter:description"]', 'content', localizedRoute.description);
  setMeta('meta[name="twitter:image"]', 'content', localizedRoute.image || BRAND_IMAGE);

  const baseFeatures = localizedRoute.featureList || [
    'FPS Mobile (Alpha) sensitivity calculator',
    'FPS Mobile (Beta) sensitivity calculator',
    'Sensi-IA aim diagnosis',
    'Mi Coach training routes'
  ];

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: 'Game Pro Hub',
        url: SITE_URL,
        logo: BRAND_IMAGE,
        sameAs: []
      },
      {
        '@type': localizedRoute.schemaType || 'WebApplication',
        '@id': `${url}#app`,
        name: localizedRoute.schemaName || 'Game Pro Hub',
        applicationCategory: 'GameApplication',
        operatingSystem: 'Web, Android, iOS',
        url,
        image: localizedRoute.image || BRAND_IMAGE,
        description: localizedRoute.description,
        publisher: { '@id': `${SITE_URL}/#organization` },
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'BRL' },
        featureList: baseFeatures
      },
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        name: 'Game Pro Hub',
        url: SITE_URL,
        inLanguage: canonicalLanguage(language),
        publisher: { '@id': `${SITE_URL}/#organization` }
      }
    ]
  };

  let schemaNode = document.getElementById('gph-public-schema');
  if (!schemaNode) {
    schemaNode = document.createElement('script');
    schemaNode.id = 'gph-public-schema';
    schemaNode.type = 'application/ld+json';
    document.head.appendChild(schemaNode);
  }
  schemaNode.textContent = JSON.stringify(schema);
};

export const LANDING_ROUTE = { kind: 'landing', path: '/' };
export const LOGIN_ROUTE_META = PUBLIC_COPY['pt-BR'].loginRoute;
export const SEO_PAGE_ROUTES = PUBLIC_COPY['pt-BR'].seoRoutes;

export const getPublicRouteConfig = (pathname = '/') => {
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (normalized === '/') return LANDING_ROUTE;
  return SEO_PAGE_ROUTES[normalized] || null;
};

const usePublicSeo = (route, language) => {
  useEffect(() => {
    document.documentElement.classList.add('gph-react-ready');
    document.documentElement.classList.add('gph-public-page');
    document.body.classList.add('gph-public-page');
    applyPublicSeoMeta(route, language);

    const unlockWrappers = () => {
      document.documentElement.style.setProperty('overflow-y', 'auto', 'important');
      document.documentElement.style.setProperty('overflow-x', 'hidden', 'important');
      document.documentElement.style.setProperty('height', 'auto', 'important');
      document.body.style.setProperty('overflow-y', 'auto', 'important');
      document.body.style.setProperty('overflow-x', 'hidden', 'important');
      document.body.style.setProperty('height', 'auto', 'important');

      ['#root', '.mi-root', '.mi-shell-content', '.app-main-layout', '.app-container'].forEach(sel => {
        document.querySelectorAll(sel).forEach(el => {
          el.style.setProperty('overflow', 'visible', 'important');
          el.style.setProperty('height', 'auto', 'important');
          el.style.setProperty('min-height', '100%', 'important');
        });
      });
    };

    unlockWrappers();
    const interval = setInterval(unlockWrappers, 300);

    return () => {
      clearInterval(interval);
      document.documentElement.classList.remove('gph-public-page');
      document.body.classList.remove('gph-public-page');

      document.documentElement.style.removeProperty('overflow-y');
      document.documentElement.style.removeProperty('overflow-x');
      document.documentElement.style.removeProperty('height');
      document.body.style.removeProperty('overflow-y');
      document.body.style.removeProperty('overflow-x');
      document.body.style.removeProperty('height');

      ['#root', '.mi-root', '.mi-shell-content', '.app-main-layout', '.app-container'].forEach(sel => {
        document.querySelectorAll(sel).forEach(el => {
          el.style.removeProperty('overflow');
          el.style.removeProperty('height');
          el.style.removeProperty('min-height');
        });
      });
    };
  }, [route, language]);
};

const SectionHeader = ({ eyebrow, title, children }) => (
  <div className="gph-public__section-header gph-reveal">
    {eyebrow && <p className="gph-public__eyebrow">{eyebrow}</p>}
    <h2>{title}</h2>
    {children && <p>{children}</p>}
  </div>
);

const PrimaryCta = ({ className = '', copy }) => (
  <a className={`gph-public__cta gph-public__cta--primary ${className}`} href={SIGNUP_HREF}>
    <span>{copy.primaryCta}</span>
    <ChevronRight size={18} aria-hidden="true" />
  </a>
);

const PublicHeader = ({ copy, language }) => {
  const { scrollDirection, isTop } = useScrollDirection({
    target: '.gph-public',
    threshold: 8,
    topOffset: 28,
  });
  const { changeLanguage } = useLang();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const currentLangCode = (String(language || '').toLowerCase().startsWith('en') ? 'en-US' : String(language || '').toLowerCase().startsWith('es') ? 'es-ES' : 'pt-BR');

  const closeMenu = () => setIsMenuOpen(false);

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeMenu();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isMenuOpen]);

  return (
    <header
      className="gph-public__nav"
      data-scroll={scrollDirection}
      data-top={isTop ? 'true' : 'false'}
      data-menu-open={isMenuOpen ? 'true' : 'false'}
    >
      <a className="gph-public__brand gph-public__brand--text" href="/" aria-label="Game Pro Hub">
        <span>Game Pro Hub</span>
      </a>

      <nav className="gph-public__nav-links" aria-label={copy.mainNavAria}>
        {copy.nav.map((item) => (
          <a key={item.href} href={item.href}>{item.label}</a>
        ))}
      </nav>

      <div className="gph-public__nav-actions">
        <div className="gph-hero__lang-pills" role="group" aria-label="Idioma">
          {SUPPORTED_LANGUAGES.map((item) => (
            <button
              key={item.code}
              type="button"
              className={`gph-hero__lang-pill ${currentLangCode === item.code ? 'is-active' : ''}`}
              onClick={() => changeLanguage && changeLanguage(item.code)}
            >
              {item.short}
            </button>
          ))}
        </div>

        <a className="gph-public__cta gph-public__cta--primary gph-public__login-cta" href="/login">
          <span>{copy.primaryCta}</span>
          <ChevronRight size={17} aria-hidden="true" />
        </a>

        <button
          type="button"
          className="gph-public__menu-toggle"
          aria-label={isMenuOpen ? 'Fechar menu' : 'Abrir menu'}
          aria-expanded={isMenuOpen}
          onClick={() => setIsMenuOpen((value) => !value)}
        >
          {isMenuOpen ? <X size={19} /> : <Menu size={19} />}
        </button>
      </div>

      <div className="gph-public__mobile-menu" aria-hidden={!isMenuOpen}>
        <nav aria-label={copy.mainNavAria}>
          {copy.nav.map((item) => (
            <a key={item.href} href={item.href} onClick={closeMenu}>{item.label}</a>
          ))}
        </nav>
        <a className="gph-public__cta gph-public__cta--primary" href="/login" onClick={closeMenu}>
          <span>{copy.primaryCta}</span>
          <ChevronRight size={17} aria-hidden="true" />
        </a>
      </div>
    </header>
  );
};


const LandingComparison = ({ extra }) => (
  <section id="comparacao" className="gph-public__section gph-public__comparison-section">
    <div className="gph-public__comparison-copy gph-reveal">
      <p className="gph-public__eyebrow">{extra.comparison.eyebrow}</p>
      <h2>{extra.comparison.title}</h2>
      <p>{extra.comparison.text}</p>
    </div>
    <div className="gph-public__comparison-grid gph-reveal delay-200">
      <article className="gph-public__comparison-list is-muted">
        <h3>{extra.comparison.leftTitle}</h3>
        <ul>{extra.comparison.left.map((item) => <li key={item}>{item}</li>)}</ul>
      </article>
      <article className="gph-public__comparison-list is-gph">
        <h3>{extra.comparison.rightTitle}</h3>
        <ul>{extra.comparison.right.map((item) => <li key={item}><CheckCircle2 size={16} aria-hidden="true" /> {item}</li>)}</ul>
      </article>
    </div>
  </section>
);

const LandingUseCaseRail = ({ extra }) => (
  <section className="gph-public__section gph-public__usecases" aria-label="Casos de uso do Game Pro Hub">
    <div className="gph-public__usecase-rail gph-reveal">
      {[...extra.useCases, ...extra.useCases].map((item, index) => (
        <span key={`${item}-${index}`} style={{ '--i': index }}>{item}</span>
      ))}
    </div>
  </section>
);

const LandingGamesSection = ({ copy }) => {
  if (!copy.games?.items?.length) return null;
  return (
    <section id="jogos" className="gph-public__section gph-public__games">
      <SectionHeader eyebrow={copy.games.eyebrow} title={copy.games.title}>
        {copy.games.body}
      </SectionHeader>
      <div className="gph-public__game-grid gph-reveal">
        {copy.games.items.map((game, index) => (
          <article className="gph-public__game-row" key={game.title}>
            <span className="gph-public__game-index">{String(index + 1).padStart(2, '0')}</span>
            <div>
              <h3>{game.title}</h3>
              <p>{game.text}</p>
            </div>
            <div className="gph-public__game-tags" aria-label={`${game.title} resources`}>
              {game.tags.map((tag) => <span key={tag}>{tag}</span>)}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

const LandingCalculatorOffers = ({ copy }) => {
  const offers = copy.calculatorOffers?.items || [];
  if (!offers.length) return null;

  return (
    <section id="calculadoras-vitalicias" className="gph-public__section gph-public__calculator-offers">
      <SectionHeader eyebrow={copy.calculatorOffers.eyebrow} title={copy.calculatorOffers.title}>
        {copy.calculatorOffers.body}
      </SectionHeader>
      <div className="gph-public__calculator-stage">
        {offers.map((offer, index) => (
          <article
            className={`gph-public__calculator-offer gph-public__calculator-offer--${offer.id} gph-reveal-stagger delay-${(index + 1) * 120}`}
            key={offer.id}
          >
            <div className="gph-public__calculator-media" aria-hidden="true">
              {offer.image ? (
                <img src={offer.image} alt="" loading="lazy" />
              ) : (
                <div className="gph-public__ff-art">
                  <span />
                  <span />
                  <span />
                </div>
              )}
              <i className="gph-public__motion-ring gph-public__motion-ring--one" />
              <i className="gph-public__motion-ring gph-public__motion-ring--two" />
              <i className="gph-public__motion-piece gph-public__motion-piece--a" />
              <i className="gph-public__motion-piece gph-public__motion-piece--b" />
            </div>
            <div className="gph-public__calculator-copy">
              <span className="gph-public__badge">{offer.badge}</span>
              <h3>{offer.name}</h3>
              <strong>{offer.price}</strong>
              <p>{offer.text}</p>
              <ul>
                {offer.features.map((feature) => (
                  <li key={feature}><CheckCircle2 size={16} aria-hidden="true" /> {feature}</li>
                ))}
              </ul>
              <a className="gph-public__cta gph-public__cta--secondary" href="/login">
                {copy.calculatorOffers.cta}
                <ChevronRight size={16} aria-hidden="true" />
              </a>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

const PublicFooter = ({ extra }) => (
  <footer className="gph-public__footer" aria-label="Rodapé do Game Pro Hub">
    <div className="gph-public__footer-main">
      <div className="gph-public__footer-brand">
        <img src="/assets/brand/gorila-text-logo.png" alt="Game Pro Hub" />
        <strong>{extra.footer.tagline}</strong>
        <p>{extra.footer.intro}</p>
      </div>
      <div className="gph-public__footer-cols">
        {extra.footer.cols.map((col) => (
          <nav key={col.title} aria-label={col.title}>
            <h3>{col.title}</h3>
            {col.links.map(([label, href]) => <a key={label} href={href}>{label}</a>)}
          </nav>
        ))}
      </div>
    </div>
    <div className="gph-public__footer-bottom">
      <p>{extra.footer.disclaimer}</p>
      <span>© {new Date().getFullYear()} Game Pro Hub · gameprohub.com.br</span>
    </div>
  </footer>
);

export const PublicLandingPage = () => {
  const { language } = useLang();
  usePublicSeo({ kind: 'landing', path: '/' }, language);

  return <LandingPageV2 />;
};

export const PublicSeoPage = ({ route }) => {
  const { language } = useLang();
  const copy = getPublicCopy(language);
  const localizedRoute = getLocalizedRoute(route, language);
  const Icon = localizedRoute.icon || Target;
  usePublicSeo(route, language);

  return (
    <div className="gph-public gph-public--seo">
      <PublicHeader copy={copy} language={language} />
      <main>
        <section className="gph-public__seo-hero">
          <div>
            <p className="gph-public__eyebrow">{localizedRoute.eyebrow}</p>
            <h1>{localizedRoute.h1}</h1>
            <p>{localizedRoute.heroText}</p>
            <div className="gph-public__hero-actions">
              <PrimaryCta copy={copy} />
              <a className="gph-public__text-link" href="/">{copy.seo.backHome}</a>
            </div>
          </div>
          <aside className="gph-public__seo-panel" aria-label={`${copy.seo.panelAriaPrefix} ${localizedRoute.h1}`}>
            <Icon size={32} aria-hidden="true" />
            <h2>{copy.seo.panelTitle}</h2>
            <ul>
              {localizedRoute.bullets.map((item) => (
                <li key={item}><CheckCircle2 size={16} aria-hidden="true" /> {item}</li>
              ))}
            </ul>
          </aside>
        </section>

        <section className="gph-public__section gph-public__seo-content">
          {localizedRoute.sections.map((section) => (
            <article key={section.title}>
              <h2>{section.title}</h2>
              <p>{section.body}</p>
            </article>
          ))}
        </section>

        <section className="gph-public__section gph-public__band">
          <SectionHeader eyebrow={copy.seo.nextEyebrow} title={copy.seo.nextTitle}>
            {copy.seo.nextBody}
          </SectionHeader>
          <div className="gph-public__seo-cta">
            <PrimaryCta copy={copy} />
            <a className="gph-public__text-link" href="/login">{copy.seo.haveAccount}</a>
          </div>
        </section>
      </main>
    </div>
  );
};
